/**
 * DIE Beschriftung jeder Geld- und Bestell-Kennzahl im Dashboard — Kacheln, Spaltenkoepfe, Tooltips
 * und das Formelblatt lesen ausschliesslich hier. Wer eine Definition aendert, aendert sie damit
 * fuer jede Stelle gleichzeitig; eine Kachel kann nicht mehr etwas anderes behaupten als ihre Spalte.
 *
 * Wortliste (Till, 2026-09-16, Plan „PLX-/Webinar-Auswertungen zentralisieren" Phase 3):
 *   Umsatz (bezahlt)  = tatsaechlich bezahlt, Gutschriften abgezogen (plx_order_metrics.actual_revenue)
 *   CPA-Wert          = Anfos x CPA-Wert des Produkts (plx_products.anfo_cpa) — Steuerungsgroesse, kein Geld
 *   Anfo-Wert         = erwarteter Zahlungseingang einer Anfo ueber 36 Monate, BRUTTO (seit 2026-10-13 Modell m5
 *                       je Bestellung, .claude/docs/anfo-wert-system.md). Die Sektion /auswertungen vergleicht
 *                       mit dem WERT BEI BESTELLUNG (Angebot, sonst Produkt; Till 2026-10-14) und zeigt die
 *                       m5-Hochrechnung nur als eigene Spalte (`TERMS.wertBeiBestellung` / `hochrechnung`).
 *   Geldbasis: alle Seiten brutto (inkl. MwSt.), AUSSER die Sektion /auswertungen — die rechnet seit
 *   2026-10-08 netto und liest dafuer `TERMS.umsatzNetto` / `anfoWertNetto` / `cpaWertNetto`.
 *   „Kundenwert (LTV)" ist RESERVIERT fuer den spaeteren Wert je Person ueber alle Produkte — nicht
 *   fuer den Anfo-Wert verwenden.
 *   Anfos             = Bestellungen (Anforderungen), nicht storniert — keine Leads, keine Testphasen-Zaehlung
 *   Registrierungen   = Personen je Webinar; Personen = ueber Webinare entdoppelt; Teilnehmer = live dabei
 * Dieselben Woerter meinen auf /webinar-auswertungen, /plx-orders, /anfo-statistics, /webinar-mailings,
 * /plx-mailings, /welcome-campaign und /sam-timeline dasselbe.
 *
 * Gegenstueck im Backend: node-app/src/helpers/plxOrderRules.ts (Regeln), webinarEvaluation.ts
 * (computeMetrics), webinarFacts.ts (Personen, Anfos), webinarMoney.ts (CPA, Anfo-Wert, bezahlt).
 * Vorgaenger: webinarKpiDefinitions.ts (2026-09-14), das jetzt nur noch re-exportiert.
 *
 * Texte sind fuer Marketing geschrieben, nicht fuer Entwickler: was die Zahl bedeutet und worauf
 * sie sich bezieht — die Formel steht getrennt daneben.
 */
import { formatNumber } from './numberUtils';

export type KpiFormat = 'int' | 'eur' | 'pct';
export type KpiGroup = 'Personen' | 'Anfos' | 'Umsatz' | 'Quoten' | 'Engagement';

export interface KpiDefinition {
  /** Voller Name (Kachel, Formelblatt). */
  label: string;
  /** Kurzform fuer Spaltenkoepfe. */
  short: string;
  group: KpiGroup;
  format: KpiFormat;
  /** Nachkommastellen; Default int 0, eur 2, pct 1. */
  decimals?: number;
  /** Was die Zahl bedeutet — erscheint als Tooltip. */
  tooltip: string;
  /** Rechenweg in Worten. */
  formel: string;
  /** Woher die Daten kommen. */
  quelle: string;
  /** Farbe fuer Geldspalten, damit CPA-Wert, Anfo-Wert und Umsatz nie verwechselt werden. */
  color?: string;
}

export const KPI_COLORS = {
  cpa: '#1b5e20',
  ltv: '#283593',
  paid: '#6d4c41',
} as const;

const Q_REG = 'Anmeldeformular der Webinar-Landingpage (adserver_webinar_registers)';
const Q_EVENTS = 'Teilnahme-Signale von StealthSeminar (adserver_ss_attendee_events)';
const Q_ORDERS =
  'Bestellungen aus PLX (plx_all_orders), dem Webinar zugeordnet über webinar_order_mapping';

/** Zusatz fuer alle Teilnahme-Kennzahlen: wo Webinare mit Tracking-Warnung mitzaehlen und wo nicht. */
const OHNE_WARNUNG =
  ' Webinare mit Tracking-Warnung zählen nur in ihrer eigenen Webinarzeile und auf ihrer Detailseite mit — in Summenzeilen, Kacheln und Kanal-Tabellen nicht.';
const OHNE_WARNUNG_FORMEL = ' (Summen: ohne Webinare mit Tracking-Warnung)';

/**
 * Fachwoerter OHNE eigene Kennzahl, fuer die Woerterzeile der Sektion (P3 „Kanäle & Werbeflächen").
 * Woerter MIT Kennzahl tragen ihren Kurzsatz als `glossar` direkt am TERMS-Eintrag — sonst stuende
 * dieselbe Erklaerung zweimal da und liefe auseinander.
 */
export const GLOSSAR = {
  heatUp: 'Heat-Up = Einladungsmail zum Webinar an unsere Liste',
  redlink: 'Redlink = Werbetext im Artikelstil im Newsletter',
  direktMailing: 'Direkt-Mailing = eigene Werbemail für ein Produkt an unsere Listen, kein Newsletter',
  bestaetigungsseite: 'Bestätigungsseite = Seite nach der Newsletter-Anmeldung',
  nachfass: 'Nachfass = Mail nach dem Webinar an die Angemeldeten',
  schwestermarken: 'Schwestermarken = Börse-Global, Börse-Express, Stock-World, Trading-Treff',
  tEuro: 'T€ = tausend Euro',
  kennung: 'Kennung = Code im Bestell-Link, der verrät, woher der Kauf kam',
  // Seit Stufe 0 des P1/P2-Umbaus (Plan 2026-10-23-p1-p2-kpi-review-umbau): die Wörterzeile nimmt nur noch die Wörter
  // ihrer Kapitel-Liste (`WOERTERZEILE`), neue Einträge erscheinen also nicht still in P3.
  // P1 (P1-V3 § 3, E7.1)
  termin: 'Termin = ein Lauf eines Webinars',
  upsell: 'Upsell = gleich den Jahresbezug gewählt, Zahlung vorab',
  einmalkauf: 'Einmalkauf = Artikel ohne Abo',
  mittlereHaelfte: 'mittlere Hälfte = Spanne, in der jeder zweite Termin landen sollte',
  kontakte: 'Kontakte = Empfänger der Einladungen bzw. Direkt-Mailings; wer mehrere bekam, zählt mehrfach',
  // P2 (P2-V3 § 4.3)
  testphase: 'Testphase = die ersten Wochen eines Abos für 0,99 €; danach kommt die erste echte Rechnung',
  sofortzahlung: 'Sofortzahlung = Angebot ohne Testphase, die erste Rechnung kommt sofort',
  plx: 'PL-X = unser Bestell- und Rechnungssystem',
  // Audit C2 (30.10.2026): Befunde und Kachel-Unterzeilen schreiben „Prozentpunkte" aus, Tabellenzellen „Pp".
  prozentpunkte: 'Pp = Prozentpunkte: Abstand zweier Quoten, 28 % gegen 24 % sind 4 Pp',
} as const;

export type GlossarKey = keyof typeof GLOSSAR;

/**
 * Rechenweg der ▲/▼-Marken für „Wie diese Sektion rechnet" (Übersicht, Audit C4 30.10.2026): Testnamen und Grenzen stehen
 * NUR hier; Tooltips und Hinweise der Karten sagen in Worten, was die Marke bedeutet, und verweisen hierher.
 */
/**
 * Quartal in Befundzeilen ausgeschrieben (Audit C7, 30.10.2026): „3. Quartal 2026". Achsen und Etiketten bleiben kurz
 * („3. Q. 26", `formatPeriode`). Nimmt `2026-Q3` (API-Schlüssel) und „Q3 2026" (Label der Verlauf-Antwort); sonst unverändert.
 */
export function quartalLang(q: string): string {
  const a = /^(\d{4})-Q([1-4])$/.exec(q);
  if (a) return `${a[2]}. Quartal ${a[1]}`;
  const b = /^Q([1-4]) (\d{4})$/.exec(q);
  if (b) return `${b[1]}. Quartal ${b[2]}`;
  return q;
}

export const RECHENWEG_TESTS =
  'Bezahlquoten zweier Zeiträume vergleicht ein Zwei-Anteile-z-Test über beide Zeiträume. Vergleicht eine Karte viele Zeilen zugleich, werden die Ergebnisse nach Holm über alle prüfbaren Zeilen der Karte korrigiert (Niveau 5 %), damit unter vielen Zeilen nicht zufällig eine auffällt. Die Bezahlquote einer Kanal- oder Produktzeile gegen die Quote aller Anfos prüft ein Binomialtest, ebenfalls nach Holm korrigiert. Anfo-Zahlen zweier gleich langer Fenster vergleicht ein Test zweier Zählwerte (Normalnäherung, Grenze |z| ≥ 1,96, zweiseitig 5 %). Ohne ▲/▼ ist ein Unterschied nicht vom Zufall zu trennen — er kann trotzdem echt sein.';

/**
 * Grundbegriffe fuer Seiten, die keine Webinar-KPIs im engeren Sinn zeigen (plx-orders,
 * anfo-statistics, Mailings, Welcome, SAM-Timeline): Label + Tooltip, damit jede Kachel dasselbe
 * sagt. Die Tooltips erklaeren die Bedeutung, nicht den Rechenweg.
 */
export const TERMS = {
  umsatz: {
    label: 'Umsatz (bezahlt)',
    short: 'Umsatz',
    tip: 'Tatsächlich bezahlter Umsatz der Anfos bis heute, brutto (inkl. MwSt.), Gutschriften abgezogen. Eine Testphasen-Rechnung bis 1 € macht aus einer Anfo kein bezahltes Abo; der Betrag ist im Umsatz enthalten. Kein Schätzwert.',
  },
  cpaWert: {
    label: 'CPA-Wert',
    short: 'CPA-Wert',
    tip: 'Jede Anfo mit ihrem CPA-Wert bewertet — dem Betrag, den eine Anfo für die Akquise wert ist. Seit 13.10.2026 trägt jede Anfo ihren eigenen Wert: bis ihre Zahlung zählt den ihres Angebots (Zahlung vorab deutlich höher als mit Testphase), danach den tatsächlich gezahlten Betrag hochgerechnet — eine nie bezahlte Anfo zählt dann 0 €. Die Werte ändern sich deshalb täglich. Steuerungsgröße für Kampagnen, kein Zahlungseingang. Anfos ohne CPA-Wert (z. B. Artikel von PL-X noch nicht geliefert) zählen 0.',
  },
  anfoWert: {
    label: 'Anfo-Wert',
    short: 'Anfo-Wert',
    tip: 'Jede Anfo mit ihrem Anfo-Wert bewertet: dem erwarteten Zahlungseingang einer Anfo über 36 Monate, brutto (inkl. MwSt.). Seit 13.10.2026 trägt jede Anfo ihren eigenen Wert: bis ihre Zahlung zählt den ihres Angebots (Zahlung vorab deutlich höher als mit Testphase), danach den tatsächlich gezahlten Betrag hochgerechnet — eine nie bezahlte Anfo zählt dann 0 €. Die Werte ändern sich deshalb täglich. Zeigt das Potenzial, nicht den Akquise-Wert — der CPA-Wert ist ein Anteil davon.',
  },
  // Sektion /auswertungen (netto seit 2026-10-08): dieselben Begriffe, Geldbasis ohne MwSt.
  umsatzNetto: {
    // P1/P2 schrieben „Bisher bezahlt" schon als Literal; seit dem P3-Review (E7, 23.10.2026) heisst es ueberall so.
    label: 'Bisher bezahlt',
    short: 'Bisher bezahlt',
    tip: 'Tatsächlich bezahlter Umsatz der Anfos bis heute, netto (ohne MwSt.), Gutschriften abgezogen. Eine Testphasen-Rechnung bis 1 € macht aus einer Anfo kein bezahltes Abo; der Betrag ist im Umsatz enthalten. Kein Schätzwert.',
    glossar: 'Bisher bezahlt = was die Anfos bis heute tatsächlich gezahlt haben, netto; wächst mit dem Alter',
  },
  anfoWertNetto: {
    label: 'Anfo-Wert',
    short: 'Anfo-Wert',
    tip: 'Der erwartete Zahlungseingang einer Anfo über 36 Monate, netto (ohne MwSt.). In dieser Sektion vergleichen alle Tabellen und Grafiken mit dem Wert bei Bestellung: dem Anfo-Wert des Angebots, ohne Angebotswert dem Einzelwert des Produkts. Er hängt nicht davon ab, was die Anfo seitdem gezahlt hat — alte und junge Anfos lassen sich damit vergleichen. Die Hochrechnung aus den eigenen Zahlungen jeder Anfo steht nur dort, wo sie so heißt. Das Modell rechnet mit den Zahlungen inkl. MwSt.; netto ist der Wert um den MwSt.-Anteil der bezahlten Anfos des Produkts gekürzt — im Schnitt rund 6 % (Inland 7 %, Ausland 0 %). Zeigt das Potenzial; was wir für eine Anfo höchstens ausgeben dürfen, ist der kleinere CPA-Wert.',
  },
  wertBeiBestellung: {
    // Name seit E7 (Till 23.10.2026): „Wert bei Bestellung 245 T€" wurde als Halbjahresumsatz gelesen.
    label: 'Erwarteter Wert (bei Bestellung)',
    short: 'Erwarteter Wert',
    // Zeitbasis (Plan 2026-10-23 Stufe 0, E8-Abgleich): bewertet wird nur, DASS bestellt wurde — mit dem HEUTIGEN Wert
    // des Angebots, auch für alte Anfos. Kein Satz darf für alte Zeiträume den Wert zum Bestellzeitpunkt behaupten.
    // B13 (Persona-Check 26.10.2026): erster Satz ohne die verdrehte Wendung „dafür, dass sie bestellt wurde“.
    tip: 'Was eine Anfo über 36 Monate voraussichtlich einbringt, netto — eine Erwartung, kein Umsatz bis heute. Bewertet wird nur, dass bestellt wurde: Gerechnet wird mit dem heutigen Anfo-Wert ihres Angebots (Werbeaktion), auch für alte Anfos; ohne Angebotswert mit dem Einzelwert ihres Produkts. Er hängt nicht davon ab, was die Anfo seitdem gezahlt hat; deshalb lassen sich alte und junge Anfos damit vergleichen. Der Wert ist ein Mittel über alle Besteller: Wer zahlt, liegt fast immer darüber, beim Jahresbezug sofort. Sofortzahlungs-Angebote über dem Preisdeckel des Modells sind noch geschätzt (Marke „geschätzt“).',
    glossar: 'Erwarteter Wert = was eine Anfo über 36 Monate erwarten lässt, netto, mit dem heutigen Wert ihres Angebots; kein Umsatz bis heute',
  },
  hochrechnung: {
    // P2-V3 § 4.2 (Klartext), sektionsweit ein Name — P1-V3 § 4 nannte noch „aus eigenen Zahlungen".
    label: 'Hochrechnung aus den bisherigen Zahlungen',
    short: 'Hochrechnung',
    tip: 'Je Anfo hochgerechnet aus dem, was sie selbst gezahlt hat: ihre Zahlung bis Tag 75, fortgeschrieben auf 36 Monate — mindestens das, was sie bis heute gezahlt hat. Eine nie bezahlte Anfo zählt 0 €. Jüngere Anfos, deren Zahlung noch nicht feststeht, tragen den Wert ihres Angebots. Netto. Nur zum heutigen Stand, nicht für einen früheren Stichtag. Für Vergleiche zwischen alten und jungen Zeiträumen nicht geeignet: alte bestehen fast nur aus echten Zahlungen, junge fast nur aus Angebotswerten.',
  },
  vorlaeufig: {
    label: 'Vorläufig ergänzt',
    short: 'vorläufig',
    tip: 'Anfos, die PL-X noch nicht vervollständigt hat: Artikel und Werbeaktion stammen vorläufig aus dem PX-Data-Export, ob gezahlt wurde, ist noch unbekannt. Sie zählen als Anfo, aber in keiner Quote und in keinem Euro-Wert — nicht in Bezahlquote, „bezahlt je Anfo“, Rechnungsstufen, Kundentyp, Erwartetem Wert und „Anteil schon bezahlt“. Sobald PL-X sie vollständig liefert, zählen sie normal.',
    glossar: 'vorläufig = Anfo, die PL-X noch nicht vollständig geliefert hat; zählt als Anfo, nicht in Quoten und Euro-Werten',
  },
  // Paket F (Till 30.10.2026): gilt nur in der Sektion /auswertungen.
  zuordnungOhneKennung: {
    label: 'Über Anmeldung (ohne Kennung)',
    short: 'ohne Kennung',
    tip: 'Eine Bestellung ohne Kennung im Bestell-Link zählt trotzdem beim Webinar, wenn dieselbe E-Mail-Adresse vor der Bestellung zu diesem Webinar angemeldet war und höchstens 72 Stunden nach Beginn bestellt hat. Passen mehrere Webinare, zählt das zuletzt gestartete. Das gilt nur in dieser Sektion: Die alten Seiten, die Nachfass-Mails und die Zahlen je Versand kennen diese Zuordnung nicht.',
  },
  nichtGezaehlt: {
    label: 'Nicht gezählt',
    short: 'nicht gezählt',
    tip: 'Zwei Sorten Bestellungen zählen in dieser Sektion nirgends: Bestellungen von Mitarbeiter-Adressen (finanztrends.de, yes-investmedia.de) und vorläufig ergänzte Anfos, deren Bestellung älter als 120 Tage ist (PL-X liefert sie dann wohl nicht mehr nach). In den gespeicherten Zahlen je Versand werden sie nicht herausgerechnet; dort sind es höchstens einzelne.',
  },
  cpaWertNetto: {
    label: 'CPA-Wert',
    short: 'CPA-Wert',
    tip: 'Jede Anfo mit ihrem CPA-Wert bewertet — dem Betrag, den eine Anfo für die Akquise wert ist, netto (ohne MwSt.). In dieser Sektion der CPA-Wert bei Bestellung: der des Angebots, ohne Angebotswert der des Produkts — unabhängig davon, was die Anfo seitdem gezahlt hat. Steuerungsgröße für Kampagnen, kein Zahlungseingang. Anfos ohne CPA-Wert (z. B. Artikel von PL-X noch nicht geliefert) zählen 0.',
  },
  // Einzelwert je Produkt (m5): nur fuer Stellen ohne einzelne Anfo — eigener Text, sonst widerspricht der Tipp der Spalte.
  einzelwertNetto: {
    // P1-V3 § 4: „Einzelwert des Produkts (Richtwert)". P2 bekommt für W1 einen eigenen Schlüssel (`wertJeAnfoModell`).
    label: 'Einzelwert des Produkts (Richtwert)',
    short: 'Einzelwert',
    tip: 'Ein Wert je Produkt, netto: die Mischung aus Angeboten mit Testphase und mit Zahlung vorab nach ihrem Anteil. Gebraucht nur, wo keine einzelne Anfo bewertet wird (Prognosen, Ranglisten, Produktübersicht). Webinar- und Mailing-Auswertungen rechnen mit dem Wert jeder einzelnen Anfo und weichen deshalb davon ab.',
  },
  einzelCpaNetto: {
    label: 'CPA-Wert netto (Einzelwert)',
    short: 'CPA Einzelwert',
    tip: 'Der CPA-Anteil des Einzelwerts je Produkt, netto. Gebraucht nur, wo keine einzelne Anfo bewertet wird.',
  },
  anfos: {
    label: 'Anfos',
    short: 'Anfos',
    // P5-Review (#7, 27.10.2026): „verweigert" fehlte — die GF las Anfo = jede Bestellung. Anfo = von PL-X angenommen.
    tip: 'Bestellungen (Anforderungen) eines Abos oder Artikels, die PL-X angenommen hat und die dieser Auswertung zugeordnet sind — auch solche, die noch in der Testphase laufen. Nicht gezählt: von PL-X verweigerte Bestellungen, stornierte Platzhalter, interne Testbestellungen und Leads (reine Newsletter-Anmeldungen). Bestellungen = Anfos + verweigerte.',
    // P2-V3 § 4.3: erweitert statt eines zweiten `GLOSSAR.anfo` (zwei Wahrheiten). Erscheint auch in der P3-Wörterzeile.
    glossar: 'Anfo = Bestellung (Anforderung), die PL-X angenommen hat, meist ein Abo mit Testphase, auch Einmalkäufe; verweigerte zählen nicht, bezahlt ist damit noch nichts',
  },
  registrierungen: {
    label: 'Registrierungen',
    short: 'Reg.',
    // E7.6: „Späteinlass" → „während des Webinars eingestiegen" (liest nur P1 und die Webinar-Detailseite der Sektion).
    tip: 'Personen, die sich für ein Webinar angemeldet haben — eine Person zählt je Webinar einmal, auch bei mehreren Anmeldezeilen. In „Anfos & Umsatz“ zählen nur Live-Anmeldungen: vor Beginn angemeldet oder während des Webinars eingestiegen (nach Beginn direkt in den laufenden Raum). Wer sich erst nach Beginn angemeldet hat und nicht mehr in den laufenden Raum kam (meist für die Aufzeichnung), steht getrennt als „zu spät für live“.',
  },
  personen: {
    label: 'Personen',
    short: 'Personen',
    tip: 'Verschiedene Menschen (E-Mail-Adressen) über alle Webinare des Zeitraums — wer zu zwei Webinaren kommt, zählt hier einmal.',
  },
  teilnehmer: {
    label: 'Teilnehmer',
    short: 'Teiln.',
    tip: 'Angemeldete Personen, die nachweislich live im Webinar waren. Jede Person zählt einmal.',
    glossar: 'Teilnehmer = Angemeldete, die nachweislich live dabei waren',
  },
  showUp: {
    // P3 (26.10.2026, B46): wie P1 „Show-up" (`showUpTermin`), nicht „Show-up-Rate" — ein Begriff, ein Name.
    label: 'Show-up',
    short: 'Show-up',
    // E7.6 (P1-V3): „Späteinlass" → „während des Webinars eingestiegen"; ändert auch den P3-Tooltip, die Zahlen nicht.
    tip: 'Anteil der vor Beginn Angemeldeten, die nachweislich live im Webinar waren. Wer während des Webinars eingestiegen ist oder sich zu spät für live angemeldet hat, zählt nicht mit. Webinare ohne verlässliche Teilnahmedaten (Tracking-Warnung, bekannte Messlücke im früheren Werkzeug oder aus anderer Quelle übernommene Anmeldungen) sind aus Zähler und Nenner ausgeschlossen. Teilnehmer sind eine Untergrenze: Wer nur die Aufzeichnung sah oder nicht erfasst wurde, fehlt — der Wert ist eher zu niedrig als zu hoch. Der eigene Webinarraum (seit Oktober 2026) misst genauer als das frühere Werkzeug.',
    glossar: 'Show-up = Anteil der vor Beginn Angemeldeten, die live dabei waren',
  },
  anfosJeRegistrierung: {
    label: 'Anfos je Registrierung',
    short: 'Anfos/Reg.',
    tip: 'Anfos ÷ Registrierungen × 100.',
  },
  anfosJeTeilnehmer: {
    label: 'Anfos je Teilnehmer',
    short: 'Anfos/Teiln.',
    // E7.3 (P1-V3): Satz statt Formel; P3 liest ihn in EinladungenKarte, die Zahlen bleiben gleich.
    tip: 'Wie viele der Teilnehmer gekauft haben, je 100. Gezählt werden nur Anfos von Personen, die live dabei waren.',
    /** P1-Beschriftung (Spalte, Detail-Textzeile); P3 schreibt weiter `${label} (je 100)`. */
    je100Label: 'Anfos je 100 Teilnehmer',
  },
  anfosJeKlick: {
    label: 'Anfos je Klick',
    short: 'Anfos/Klick',
    tip: 'Anfos ÷ eindeutige Klicker × 100.',
  },
  anfosJeEmpfaenger: {
    label: 'Anfos je Empfänger',
    short: 'Anfos/Empf.',
    tip: 'Anfos ÷ Empfänger × 100.',
  },
  etkp: {
    label: 'ETKP',
    short: 'ETKP',
    tip: 'CPA-Wert je 1.000 Empfänger (Ertrag je Tausend Kontakte).',
  },
  // ── Sektion „Anfos & Umsatz" (/auswertungen), Definitionen in node-app/src/helpers/anfoFacts.ts ──
  bezahlquote: {
    label: 'Bezahlquote',
    short: 'Bezahlquote',
    // F2 (Till 23.10.2026): Reife 60 statt 30 Tage für alle Bezahlquoten der Sektion (`MATURE_DAYS`, anfoFacts.ts).
    tip: 'Anteil der Anfos mit einer Zahlung über 1 € (die 0,99-€-Testphasenrechnung zählt nicht). Gerechnet nur über Anfos, die mindestens 60 Tage alt sind. Danach kommt kaum noch eine erste Zahlung dazu (gemessen bis 365 Tage); deshalb lässt sich die Quote über die Zeit vergleichen.',
    // B13: steht in der P2-Wörterzeile (die Anfo-Erklärung verweist darauf).
    glossar: 'Bezahlquote = Anteil der Anfos ab 60 Tagen mit einer Zahlung über 1 €',
  },
  reife: {
    label: 'Reife',
    short: 'Reife',
    tip: 'Junge Bestellungen haben ihre Rechnungen noch nicht bekommen: die erste kommt nach der 30-tägigen Testphase, jede weitere ein Quartal später. Bezahlter Umsatz junger Zeiträume ist deshalb immer zu niedrig. Solche Zeiträume sind grau markiert.',
  },
  // `label`/`short` der Kundentypen lesen die alten Seiten (/plx-orders, /webinar-statistics) — nicht ändern; die Sektion
  // liest `sektionLabel` (P2-V3 § 4.3, ein Name in Tabelle und Grafik).
  erstbesteller: {
    label: 'Erstbesteller',
    short: 'Erstbesteller',
    tip: 'Diese Adresse hat vorher noch keine Anfo abgegeben. Bestellungen derselben Sitzung (binnen 2 Stunden, z. B. Doppelklick) zählen nicht als „vorher“.',
    sektionLabel: 'Erstbesteller',
  },
  frueherGezahlt: {
    label: 'Früher gezahlt',
    short: 'Früher gezahlt',
    tip: 'Diese Adresse hat schon früher bestellt, und mindestens eine dieser früheren Anfos ist nach heutigem Stand bezahlt (über 1 €).',
    sektionLabel: 'Hat schon einmal gezahlt',
  },
  frueherNieGezahlt: {
    label: 'Früher bestellt, nie gezahlt',
    short: 'Nie gezahlt',
    tip: 'Diese Adresse hat schon früher bestellt, aber keine der früheren Anfos ist bis heute bezahlt.',
    sektionLabel: 'Früher bestellt, nie gezahlt',
  },
  seriellerNichtzahler: {
    label: 'Serieller Nichtzahler',
    short: 'Seriell',
    // „30 Tage" bleibt: die Grenze der seriellen Nichtzahler ist bei F2 NICHT mitgewandert (`SERIAL_NONPAYER_MIN_AGE_DAYS`).
    tip: 'Adresse mit mindestens zwei früheren Anfos, die alle älter als 30 Tage und unbezahlt sind, und keiner bezahlten. Jüngere frühere Anfos zählen nicht — deren Rechnung kann noch kommen.',
    /** P2 K3, Unterzeile am Kundentyp; `{n}` ersetzt die Karte. */
    unterzeile: 'davon {n} mit mindestens zwei früheren Bestellungen, alle über 30 Tage alt und nie bezahlt',
  },
  naechsteRechnung: {
    label: 'Nächste Rechnung',
    short: 'Nächste Rechnung',
    tip: 'Je laufendem Vertrag nur die NÄCHSTE fällige Rechnung, sofern sie in die kommenden 12 Monate fällt: Quartalspreis der Werbeaktion (beim Jahresbezug das Vierfache), netto. Spätere Rechnungen desselben Vertrags zählen nicht. „Erwartet“ gewichtet mit der Zahlwahrscheinlichkeit aus der Mahnstufe — dieselbe Rechenregel wie die Cashflow-Seite; die zeigt aber nur 13 Wochen und brutto.',
  },
  erreicht: {
    label: 'Erreicht',
    short: 'Erreicht',
    // Begründung für „über 100 %" gemessen 23.10.2026 (Abgleich E8/P2): 2025 zahlten mehr Besteller als heute, zu denselben Preisen.
    tip: 'Bisher bezahlt geteilt durch den heutigen Wert des Angebots derselben Anfos (beides netto). Ohne Einmalkäufe und ohne vorläufig ergänzte Anfos, deren Zahlstand noch unbekannt ist. Junge Quartale haben noch wenig zahlen können. Über 100 % in älteren Quartalen ist möglich: Der Wert ist aus den Zahlungen der letzten Monate gerechnet. 2025 haben mehr Besteller gezahlt als heute, 2024 haben Zahler zudem länger verlängert, und Anfang 2024 galten andere Preise.',
  },
  vertragsstand: {
    label: 'Vertragsstand',
    short: 'Vertrag',
    tip: 'Was PL-X heute zum Vertrag meldet: läuft und zahlt, läuft und ist gekündigt (zahlt bis zum Ende), läuft noch ohne Zahlung (meist Testphase), beendet oder widerrufen. Einmalkäufe haben keinen laufenden Vertrag und stehen getrennt.',
    glossar: 'Vertragsstand = was PL-X heute zum Vertrag meldet: läuft, gekündigt, beendet oder widerrufen',
  },
  doppelbestellung: {
    label: 'Doppelbestellung',
    short: 'Doppelt',
    tip: 'Dieselbe Adresse bestellt denselben Artikel binnen 7 Tagen nach ihrer vorigen Bestellung noch einmal. PL-X storniert die meisten solcher Bestellungen selbst; hier zählen nur die, die als Anfo stehen bleiben.',
  },
  rechnungsstufe: {
    label: 'Rechnungsstufe',
    short: 'Rechnung',
    tip: 'Was PL-X bisher zu einer Anfo meldet: bezahlt (über 1 €), nur die 0,99-€-Testphasenrechnung, fakturiert aber nichts gezahlt, oder noch keine Rechnung. Ohne Rechnung unterscheidet der Vertragsstand: läuft noch (Rechnung steht aus, meist Testphase) oder widerrufen bzw. beendet, ohne je fakturiert zu werden. PL-X liefert keine Rechnungshistorie, deshalb keine Zahl „Rechnungen je Kunde“.',
    glossar: 'Rechnungsstufe = wie weit eine Anfo gekommen ist: bezahlt, nur die Testphase, Rechnung ohne Zahlung, vor der ersten Rechnung beendet',
  },
  upsellQuote: {
    label: 'Upsell-Quote',
    short: 'Upsell',
    tip: 'Anteil der Anfos, die als Jahresbezug (Upsell) abgeschlossen wurden, an allen Anfos. Die Upsell-Bestellung ersetzt in den PL-X-Daten die Erstbestellung — es gibt also keine zwei Zeilen je Kunde.',
  },
  listenpreis: {
    label: 'Listenpreis',
    short: 'Listenpreis',
    tip: 'Konfigurierter Quartalspreis der Werbeaktion zum Bestelltag. Die Preis-Historie wurde einmal am 17.11.2025 angelegt; für Werbeaktionen, die sie nicht kennt (ältere wie neuere), steht der heutige Preis — diese Anfos sind als „heutiger Preis“ gezählt.',
  },
  kanal: {
    label: 'Kanal',
    short: 'Kanal',
    tip: 'Woher die Bestellung kam, erkannt an der Kennung (Banner) im Bestell-Link. Eine Bestellung hat genau einen Kanal. Die Regeln stehen auf der Seite „Anfos & Umsatz – Übersicht“.',
    // P3: Zeile unter der Leitfrage (vorschlag_v3 §4.1), zusammen mit `werbeflaeche.glossar`.
    glossar: 'Kanal = grober Weg der Bestellung (z. B. Newsletter)',
  },
  // Kapitel „Webinare & Produkte" (P1)
  kontakteRegulaer: {
    label: 'Kontakte',
    short: 'Kontakte',
    tip: 'Empfänger der Webinar-Einladungen (Heat-Ups, Redlinks) bzw. der Direkt-Mailings. Versände an die ganze Liste (früher samstags) zählen nur mit ihren aktiven Empfängern; Non-Opener-Nachversände zählen nicht. Ein Empfänger mehrerer Einladungen zählt mehrfach — es sind Kontakte, keine Personen. Kontaktzahlen gibt es für Webinar-Einladungen ab 10.12.2025, für Direkt-Mailings ab 23.12.2025; der Produktvergleich je 1.000 Kontakte beginnt für beide am 23.12.2025.',
  },
  anfosJe1k: {
    label: 'Anfos je 1.000 Kontakte',
    short: 'Anfos/1.000',
    tip: 'Wie viele Anfos 1.000 Kontakte gebracht haben. Gezählt wird je Versand die erste Anfo einer Person, die sich über diesen Versand angemeldet hat bzw. über sein Mailing bestellt hat.',
  },
  wertJe1k: {
    label: 'Erwarteter Wert je 1.000 Kontakte',
    short: '€ je 1.000',
    tip: 'Anfos je 1.000 Kontakte × durchschnittlicher Wert bei Bestellung (netto) dieser Anfos. Eine Erwartung über 36 Monate, kein Umsatz bis heute — was schon gezahlt wurde, steht als „bisher bezahlt je 1.000“ daneben. Sagt, was 1.000 Kontakte erwartungsgemäß einbringen — vergleichbar über Produkte mit verschiedenem Preis und Bezahlverhalten und über alte und junge Webinare.',
  },
  bezahltJe1k: {
    label: 'Bisher bezahlt je 1.000 Kontakte',
    short: 'bezahlt je 1.000',
    tip: 'Echtes Geld: was die Anfos des ersten Kontakts bis heute gezahlt haben (netto), je 1.000 Kontakte. Junge Webinare liegen hier immer niedrig — ihre Rechnungen kommen erst nach der Testphase und dann quartalsweise.',
  },
  geschrumpft: {
    label: 'Vorsichtiger Wert',
    short: 'vorsichtig',
    tip: 'Der eigene Wert, zum Schnitt des Produkts hin korrigiert — umso stärker, je weniger Kontakte dahinterstehen. Ein kleines Webinar mit zufällig vielen Anfos landet so nicht vorn. Hat ein Produkt nur ein Webinar, wird zum Schnitt aller Webinare hin korrigiert. Danach wird gerankt.',
  },
  ruhetage: {
    label: 'Ruhetage',
    short: 'Ruhetage',
    tip: 'Tage seit dem letzten Webinar des Produkts. Ein Produkt, das gerade lief, ist kein Kandidat — dieselbe Liste würde zu schnell wieder angeschrieben.',
  },
  anfosJe100Anm: {
    label: 'Anfos je 100 Anmeldungen',
    short: 'Anfos/100 Anm.',
    // E7.6: Wortwahl „während des Webinars eingestiegen" / „zu spät für live" (liest nur P1).
    tip: 'Alle Anfos des Webinars ÷ Personen mit Live-Anmeldung (vor Beginn angemeldet oder während des Webinars eingestiegen) × 100. Die Anfos enthalten auch Käufe ohne Anmeldung und von Personen, die sich zu spät für live angemeldet haben (z. B. für die Aufzeichnung) — diese Personen zählen aber nicht als Anmeldung. Deshalb ist die Zahl keine Stufe eines Trichters.',
  },
  // Kapitel „Kanäle & Werbeflächen" (P3, Plan 2026-10-20)
  kanalGruppe: {
    label: 'Kanalgruppe',
    short: 'Gruppe',
    tip: 'Mehrere verwandte Kanäle zusammengefasst (z. B. Newsletter = Anzeigen und Redlinks im Newsletter). Jede Gruppe hat auf allen Karten dieselbe Farbe; aufklappen zeigt die einzelnen Kanäle.',
  },
  werbeflaeche: {
    label: 'Werbefläche',
    short: 'Fläche',
    tip: `Ein bestimmter Platz, an dem geworben wird, mit eigener Empfängerzahl: z. B. Heat-Up am Vortag, Redlink im Newsletter, Direkt-Mailing an die Finanztrends-Liste. Feiner als der Kanal. ${GLOSSAR.heatUp} · ${GLOSSAR.redlink} · ${GLOSSAR.nachfass}.`,
    glossar: 'Werbefläche = einzelner Werbeplatz mit eigener Empfängerzahl (z. B. Heat-Up am Vortag)',
  },
  kontakteZustellungen: {
    label: 'Kontakte',
    short: 'Kontakte',
    tip: 'Zugestellte Mails (bzw. Push-Nachrichten) der Fläche im Fenster — Zustellungen, keine Personen: wer drei Heat-Ups bekam, zählt dreimal. Versände an die ganze Liste (früher samstags) zählen nur mit ihren aktiven Empfängern. Versände der letzten 14 Tage zählen noch nicht, weil ihre Anfos noch kommen.',
    glossar:
      'Kontakte = zugestellte Mails bzw. Push-Nachrichten, keine Personen; bei Versänden an die ganze Liste nur die aktiven Empfänger',
  },
  anfosJe1kFlaeche: {
    label: 'Anfos je 1.000 Kontakte',
    short: 'Anfos/1.000',
    tip: 'Anfos dieser Fläche je 1.000 Kontakte. Webinar-Einladungen: die erste Anfo einer Person je Webinar, gebucht auf die Fläche, über die sie sich zuerst angemeldet hat. Direkte Bestell-Links: die Anfos mit der Kennung der Fläche im Bestell-Link.',
  },
  // Workshop-Lücken 29.10.2026 (Plan 2026-10-29), Kapitel Kanäle: E2 Vorjahreslinie, N4 Rückblick „Letzte Woche".
  vorjahrLinie: {
    label: 'Vorjahr (gestrichelt)',
    short: 'Vorjahr',
    tip: 'Derselbe Monat ein Jahr früher, für die gewählten Kanalgruppen zusammen (Linienansicht: je Gruppe). Der laufende Monat wird mit dem Vorjahresmonat bis zum selben Tag verglichen. Bei „Anteil %“ der Anteil an allen Anfos (bzw. am ganzen Erwarteten Wert) des Vorjahresmonats — erst, wenn Gruppen abgewählt sind; mit allen Gruppen wäre er immer 100 %. Bei „Bisher bezahlt“ keine Linie: der Vorjahresmonat hatte zwölf Monate mehr Zeit zum Zahlen.',
  },
  rueckblickBisher: {
    label: 'Anfos je 1.000 bisher',
    short: 'je 1.000 bisher',
    tip: 'Anfos dieses Versands je 1.000 Kontakte bis heute (ohne vorläufig ergänzte). Die Woche ist jung: es kommen noch Anfos — deshalb steht daneben der Vergleich bei gleichem Alter, nicht der Endwert früherer Versände.',
  },
  rueckblickVergleich: {
    label: 'Schnitt der 12 Wochen davor, gleiches Alter',
    short: 'Schnitt 12 Wochen',
    tip: 'Alle Versände derselben Fläche in den 12 Wochen vor der gewählten Woche zusammen: ihre Anfos bis zum selben Alter (Tage nach dem Versand) wie dieser Versand heute, je 1.000 ihrer Kontakte. In der Summenzeile gewichtet mit den Kontakten der Versände dieser Woche.',
  },
  rueckblickAlter: {
    label: 'Alter',
    short: 'Alter',
    tip: 'Tage seit dem Versandtag (bzw. dem Webinartag) bis heute. Bis zu diesem Alter zählen auch die Anfos des Vergleichs.',
  },
  rueckblickWertVorlaeufig: {
    label: 'Erwarteter Wert bisher (vorläufig)',
    short: '€ bisher (vorläufig)',
    tip: 'Erwarteter Wert (bei Bestellung, netto) der Anfos bis heute. Vorläufig, weil die Woche jung ist und noch Anfos kommen; vorläufig ergänzte Anfos zählen 0 €.',
  },
  rueckblickAnmeldungen: {
    label: 'Anmeldungen je 1.000 Kontakte',
    short: 'Anm./1.000',
    tip: 'Personen mit Live-Anmeldung, gebucht auf die Welle und Liste ihrer ersten Anmeldung, je 1.000 Kontakte dieser Liste in dieser Welle. Mit Webinarbeginn fertig. Daneben derselbe Wert derselben Liste und Welle über die 8 Webinare davor (gelaufen, alle drei Wellen).',
  },
  rueckblickWebinarAnfos: {
    label: 'Anfos des Webinars bisher',
    short: 'Anfos bisher',
    tip: 'Alle Anfos, die dem Webinar zugeordnet sind (auch ohne Anmeldung über einen Heat-Up), bis heute. Daneben der Schnitt der 8 Webinare davor, jedes nur bis zum selben Alter seit seinem Webinartag. Je Welle gibt es keine Anfos: der Versand-Cache kennt keine Bestellzeit.',
  },
  wertJe1kFlaeche: {
    // Gleiche Beschriftung wie P1 (`wertJe1k`) — dieselbe Größe, nur je Fläche statt je Produkt.
    label: 'Erwarteter Wert je 1.000 Kontakte',
    short: '€ je 1.000',
    tip: 'Wert bei Bestellung dieser Anfos (netto) je 1.000 Kontakte. Webinar-Einladungen: Anfos × mittlerer Wert bei Bestellung der Anfos ihres Webinars (wie auf der Kandidaten-Karte). Kein bezahlter Umsatz — die Flächen sind verschieden alt, bezahlt würde junge Flächen schlechtrechnen.',
  },
  abmeldungenJe1k: {
    label: 'Abmeldungen je 1.000 Kontakte',
    short: 'Abm./1.000',
    tip: 'Wie viele Empfänger sich über diese Mail abgemeldet haben, je 1.000 Kontakte — der Preis, den die Fläche an Liste kostet. Nur wo die Fläche die ganze Mail ist; bei Newsletter-Plätzen gehört die Abmeldung der Ausgabe, nicht dem Platz.',
  },
  liveAnmeldungen: {
    label: 'Live-Anmeldungen',
    short: 'Live-Anm.',
    tip: 'Anmeldungen vor Sendestart oder per Späteinlass in den laufenden Raum. Wer sich erst nach Sendestart angemeldet hat (meist für die Aufzeichnung), steht getrennt.',
  },
  wenigDaten: {
    label: 'Wenig Daten',
    short: 'wenig Daten',
    // Die Schwellen kommen aus der API (je Größe verschieden); die Karte haengt sie an diesen Satz an.
    tip: 'Zu wenige Fälle für eine belastbare Quote: der Wert kann am Zufall hängen. Solche Werte stehen grau und beim Sortieren am Ende.',
    /** Etikett am Zeilennamen, je nach Grund der Sortierspalte. */
    etikett: {
      anfos: 'wenig Anfos',
      anmeldungen: 'wenig Anmeldungen',
      abmeldungen: 'wenig Abmeldungen',
    },
  },
  blogDerBestellung: {
    label: 'Blog/Portal der Bestellung',
    short: 'Blog',
    // Nur P3 liest diesen Begriff; Text nach vorschlag_v3 §4.4 (die Quellen-Effizienz ist nicht mehr der Verweis).
    // P3 (26.10.2026, B60): „fast immer Finanztrends“ stimmte seit Dezember 2025 für jede sechste Webinar-Anfo nicht.
    tip: 'Die Marke, über die die Bestellung lief — aus der Kampagnen-Kennung der Bestellseite, sonst aus dem Präfix des Banners. Webinar-Bestellungen tragen meist die Finanztrends-Kennung des Webinarraums; kam die Einladung über einen Heat-Up einer Schwestermarke, trägt der Kauf seit Dezember 2025 oft deren Kennung. Welche Einladung jemanden zum Webinar gebracht hat, zeigt die Karte „Webinar-Einladungen“.',
  },
  bezugsart: {
    label: 'Bezugsart',
    short: 'Bezugsart',
    tip: 'Wie die Anfo bezogen wird: Upsell (Jahresbezug, Zahlung vorab), Quartalsbezug (mit oder ohne Testphase), Einmalkauf, noch offen (PL-X hat die Werbeaktion noch nicht geliefert) oder vorläufig ergänzt (Werbeaktion nur vorläufig bekannt).',
  },
  anmeldequote: {
    label: 'Anmeldequote 30 Tage',
    short: 'Anmeldequote',
    tip: 'Anteil der neuen Finanztrends-Leads eines Monats, die sich in den 30 Tagen nach ihrer Anmeldung bei Finanztrends zum ersten Mal live für ein Webinar angemeldet haben — rechtzeitig vor Sendestart oder per Späteinlass in den laufenden Raum. Wer sich nur nach Sendestart angemeldet hat (meist für die Aufzeichnung), zählt nicht mit und steht getrennt.',
  },
  // Kapitel „Kanäle & Werbeflächen" nach dem KPI-Review (Plan 2026-10-23, Spezifikation vorschlag_v3 §3.3).
  // Saetze, die nur fuer eine P3-Spalte gelten, haengen als `hinweis` an der Spalte, nicht hier.
  anteilAnAllenAnfos: {
    label: 'Anteil an allen Anfos',
    short: 'Anteil',
    tip: 'Anteil dieser Zeile an allen Anfos des Zeitraums.',
  },
  anteilErstbesteller: {
    label: 'Anteil Erstbesteller',
    short: 'Erstbesteller',
    tip: 'Anteil der Anfos von Adressen, die vorher noch nie eine Anfo abgegeben hatten. Bestellungen derselben Sitzung (binnen 2 Stunden) zählen nicht als „vorher“; gezählt wird seit Datenbeginn 12/2023. Hoch heißt: Der Kanal bringt neue Besteller. Kein Qualitätsmaß — Erstbesteller zahlen seltener als Kunden, die früher schon gezahlt haben. Ohne vorläufig ergänzte Anfos.',
  },
  veraenderungVorjahr: {
    label: 'Veränderung zum Vorjahr',
    short: 'ggü. Vorjahr',
    tip: 'Erwarteter Wert dieses Zeitraums gegen denselben Zeitraum ein Jahr früher — das gleicht die Saison aus. Verglichen werden die Zahl der Anfos und der erwartete Wert je Anfo; die Veränderung ist beides zusammen. Anfos ohne Wert zählen so nicht als 0 €, vorläufig ergänzte gar nicht. Der erwartete Wert hängt nicht vom Alter der Anfos ab; bezahlter Umsatz würde den jüngeren Zeitraum schlechter rechnen. Kurze Zeiträume schwanken stark.',
  },
  veraenderungVorzeitraum: {
    label: 'Veränderung zum Vorzeitraum',
    short: 'ggü. Vorzeitraum',
    tip: 'Erwarteter Wert dieses Zeitraums gegen den gleich langen Zeitraum direkt davor — zeigt die jüngste Bewegung, enthält aber die Saison: Der Jahresanfang ist regelmäßig stark. Verglichen werden die Zahl der Anfos und der erwartete Wert je Anfo; die Veränderung ist beides zusammen. Anfos ohne Wert zählen so nicht als 0 €, vorläufig ergänzte gar nicht. Kurze Zeiträume schwanken stark, und Verschiebungen zwischen Kanälen können auch aus geänderten Kennungen in den Bestell-Links kommen.',
  },
  wertSumme: {
    label: 'Erwarteter Wert (Summe)',
    short: 'Summe',
    tip: 'Erwarteter Wert (bei Bestellung) aller Anfos der Zeile zusammen, netto über 36 Monate — eine Erwartung, kein Umsatz bis heute. Anfos ohne Wert und vorläufig ergänzte zählen 0 €.',
  },
  anmeldungenJe1k: {
    label: 'Anmeldungen je 1.000 Kontakte',
    short: 'Anm. je 1.000',
    tip: 'Live-Anmeldungen je 1.000 Kontakte: vor Beginn des Webinars oder per Späteinlass in den laufenden Raum. Gerechnet über dieselben Webinare wie Show-up und Teilnehmer; Anmeldungen × Show-up ergibt deshalb ungefähr die Teilnehmer. Wer sich erst nach Beginn angemeldet hat, meist für die Aufzeichnung, zählt nicht mit; der Anteil steht darunter. Er wird erst seit 25.08.2026 erfasst und deshalb nur über Webinare ab diesem Tag gerechnet.',
  },
  anmeldungenFlaeche: {
    label: 'Anmeldungen',
    short: 'Anm.',
    tip: 'Live-Anmeldungen (vor Beginn oder Späteinlass): eine Person je Webinar, gezählt bei der Werbefläche ihrer ersten Anmeldung; alle Webinare. Anmeldungen nach Beginn stehen getrennt.',
  },
  teilnehmerJe1k: {
    label: 'Teilnehmer je 1.000 Kontakte',
    short: 'Teiln. je 1.000',
    tip: 'Wie viele Angemeldete je 1.000 Kontakte nachweislich live im Webinar waren, einschließlich Späteinlass. Gerechnet über dieselben Webinare wie Anmeldungen und Show-up, also nur Webinare mit verlässlichen Teilnahmedaten. Eine Untergrenze. Lassen sich die Kontakte nicht nach Webinar trennen, steht „—“.',
  },
  anfosJe100AnmFlaeche: {
    label: 'Anfos je 100 Anmeldungen (erste Anmeldung)',
    short: 'Anfos/100 Anm.',
    tip: 'Anfos der Live-Angemeldeten je 100 Live-Anmeldungen, gezählt bei der Werbefläche, über die sich eine Person zuerst angemeldet hat; alle Webinare. Anders als „Anfos je 100 Anmeldungen“ der Webinar-Karten: dort zählen alle Anfos des Webinars.',
  },
  wertJeAbmeldung: {
    label: 'Wert je Abmeldung',
    short: '€ je Abm.',
    tip: 'Erwarteter Wert je Empfänger, der sich über diese Mail abgemeldet hat: was die Fläche einbringt für das, was sie an Liste kostet. Hängt nicht davon ab, wie die Kontakte gezählt werden. Nur wo die Fläche die ganze Mail ist.',
  },
  showUpIndex: {
    label: 'Show-up-Index',
    short: 'Index',
    tip: 'Wie viele Angemeldete dieser Werbefläche live dabei waren, verglichen mit dem Schnitt derselben Webinare (1,00 = Schnitt). Gleicht aus, dass Webinare verschieden gut besucht sind und dass der eigene Webinarraum die Teilnahme genauer misst als das frühere Werkzeug.',
  },
  abmeldungenAnzahl: {
    label: 'Abmeldungen',
    short: 'Abm.',
    tip: 'Empfänger, die sich über diese Mail abgemeldet haben. Nur wo die Fläche die ganze Mail ist; bei Newsletter-Plätzen gehört die Abmeldung der Ausgabe, nicht dem Platz.',
  },
  versaende: {
    label: 'Versände',
    short: 'Versände',
    tip: 'Wie viele Mails bzw. Push-Kampagnen dieser Fläche im Zeitraum verschickt wurden — Kampagnen, nicht Empfänger.',
  },
  bezahltJeAnfo: {
    label: 'Bisher bezahlt je Anfo',
    short: 'bezahlt je Anfo',
    tip: 'Was eine Anfo bis heute im Schnitt tatsächlich bezahlt hat, netto. Echtes Geld, aber es wächst mit dem Alter der Anfos — deshalb steht es nur zusammen mit den Zahlern und dem Ø Alter, und Zeilen lassen sich damit nur bei ähnlichem Alter vergleichen.',
  },
  abweichungMarke: {
    label: 'Auffällige Abweichung',
    short: '▲/▼',
    tip: '▲/▼: Die Bezahlquote dieser Zeile weicht auffällig vom Schnitt aller Anfos ab, auch wenn man berücksichtigt, dass hier viele Zeilen zugleich verglichen werden. Ohne Marke ist der Unterschied nicht von Zufall zu trennen.',
  },
  direktvergleichHeatUps: {
    label: 'Direktvergleich auf denselben Webinaren',
    short: 'Direktvergleich',
    // P3 (26.10.2026, B65): nennt, wonach „vorn" zählt, und die Schlagseite der Zuordnung zugunsten des Vortags.
    tip: 'Vergleicht die beiden laufenden Heat-Ups nur auf Webinaren, zu denen beide verschickt wurden und die keine Tracking-Warnung haben. Die Tabelle darüber mischt verschiedene Webinare: Nicht jedes Webinar hatte beide Heat-Ups. „Vorn“ zählt nur nach Teilnehmern je 1.000 Kontakte; der erwartete Wert kann anders ausfallen. Gezählt wird bei der Einladung, über die sich eine Person zuerst angemeldet hat — das bevorzugt den früheren Vortag. Ein niedrigerer Wert des Webinartags heißt deshalb nicht, dass er verzichtbar ist.',
  },
  // ── P1 + P2 gemeinsam (Plan 2026-10-23-p1-p2-kpi-review-umbau, Stufe 0) ─────────────────────────────────────
  // Kein Satz nennt eine Zahl, die sich mit Daten oder Modell ändert (P1-V3 § 2.0/10, P2-V3 § 2.0/7); Schwellen
  // kommen aus der API und werden von der Karte angehängt (Muster `jungTip`).
  abweichungZeitraumMarke: {
    label: 'Auffällige Veränderung',
    short: '▲/▼',
    // P2-V3 § 2.0 Regel 8: Zwei-Anteile-Test über BEIDE Zeiträume (`zweiAnteileZTest`), Holm über die Familie der Karte.
    tip: '▲/▼: deutlich anders als im Vergleichszeitraum, geprüft mit einem Test über beide Zeiträume — auch wenn man berücksichtigt, dass hier mehrere Zeilen zugleich verglichen werden. Ohne Marke ist der Unterschied nicht von Zufall zu trennen.',
  },
  wertGeschaetzt: {
    label: 'geschätzt',
    short: 'geschätzt',
    // Till 23.10.2026: Marke an Angeboten über dem m5-Preisdeckel (Regel `wertUeberPreisdeckel`, anfoWertModelM5.ts).
    tip: 'Der Erwartete Wert dieses Angebots ist noch geschätzt: Es ist ein Angebot mit Sofortzahlung, dessen Preis über dem Deckel liegt, bis zu dem das Anfo-Wert-Modell den Wert aus eigenen Zahlungen ableiten kann. Für den Startwert rechnet das Modell solche Angebote so, als kosteten sie genau den Deckel. Testphasen-Angebote und Einmalkäufe betrifft das nie. Der Wert wird genauer, sobald genug eigene Zahlungen zu solchen Angeboten vorliegen.',
    glossar: 'geschätzt = Erwarteter Wert eines Sofortzahlungs-Angebots über dem Preisdeckel des Modells; wird genauer, sobald genug eigene Zahlungen da sind',
  },
  heutigerAngebotswert: {
    // E8-Abgleich (review-p3 e8/abgleich_p2.md § 6): K4a-Kopf; für alte Quartale ist der Wert der HEUTIGE.
    label: 'Erwarteter Wert (heutiger Angebotswert)',
    short: 'heutiger Angebotswert',
    tip: 'Erwarteter Wert dieser Anfos über 36 Monate, netto, bewertet mit dem heutigen Wert ihres Angebots — auch für alte Quartale. Bewertet wird nur, dass bestellt wurde; frühere Bewertungen der Angebote spielen keine Rolle. Der heutige Wert ist aus dem Zahlverhalten der letzten Monate gerechnet.',
  },
  jung: {
    label: 'jung',
    short: 'jung',
    // P2-V3 § 2.0/3 und § 4.4; mit den Schwellen der API über `jungTip(anteil, tage)`.
    tip: 'Ein großer Teil der Anfos dieser Zeile ist noch jünger als die Reife. Bis dahin kommen noch erste Zahlungen; die Quote steigt meist noch. Anfos, Erwarteter Wert und Veränderung hängen nicht vom Alter ab und sind deshalb nie „jung“.',
  },
  datenstandBestellsystem: {
    label: 'Datenstand Bestellsystem',
    short: 'Datenstand',
    tip: 'Jüngste Bestellung, die PL-X geliefert hat (zweimal täglich, gegen 7 und 14 Uhr). Zeiträume enden hier, das Vorjahr am selben Tag zur selben Uhrzeit.',
  },
  // --- P1 --- (Kapitel „Webinare & Produkte", P1-V3 § 3/§ 4; spätere Zusätze des P1-Bauers NUR in diesem Block)
  zuSpaetLive: {
    label: 'zu spät für live',
    short: 'zu spät für live',
    tip: 'Personen, die sich erst nach Beginn angemeldet haben und nicht mehr in den laufenden Raum kamen, meist für die Aufzeichnung. Sie zählen nicht als Anmeldung. Erst seit 25.08.2026 gespeichert.',
    // Persona-Check 26.10.2026 (B29): steht in jeder Zeile der Webinar-Tabelle, gehört in die Wörterzeile.
    glossar: 'zu spät für live = nach Beginn angemeldet und nicht mehr live dabei (meist für die Aufzeichnung); zählt nicht als Anmeldung',
  },
  eingestiegen: {
    label: 'während des Webinars eingestiegen',
    short: 'eingestiegen',
    tip: 'Nach Beginn angemeldet und direkt in den laufenden Raum geleitet. Zählt als Anmeldung und als Teilnehmer, nicht im Show-up.',
    glossar: 'eingestiegen = während des Webinars angemeldet und gleich in den laufenden Raum gekommen; zählt als Anmeldung und Teilnehmer, nicht im Show-up',
  },
  abmeldungenJe1kVersand: {
    // Nur P1; `abmeldungenJe1k` bleibt für P3 (dort „Fläche"/„Newsletter-Plätze").
    label: 'Abmeldungen je 1.000 Kontakte',
    short: 'Abm. je 1.000',
    tip: 'Wie viele Empfänger sich über die Einladungs-Mails bzw. das Direkt-Mailing abgemeldet haben, je 1.000 Kontakte. Non-Opener-Nachversände fehlen in Zähler und Nenner. Bei Versänden an die ganze Liste zählt der Zähler alle Abmeldungen, der Nenner nur die aktiven Empfänger.',
  },
  obergrenzeEinladung: {
    // E3 (Till 23.10.): benannte Ausnahme vom Attributionsvertrag.
    label: 'bis … mit allen Webinar-Käufen',
    short: 'Obergrenze',
    tip: 'So hoch wäre der Wert, wenn man alle Käufe zählt, die dem Webinar zugeordnet sind, auch über Aufzeichnung und Anzeigen. Das ist zu hoch, denn diese Käufe kamen nicht über die Kontakte im Nenner. Der wahre Wert der Einladung liegt dazwischen.',
  },
  anfosUeberEinladung: {
    label: 'Anfos über die Einladung',
    short: 'über die Einladung',
    tip: 'Die erste Anfo einer Person je Webinar, gebucht auf die Einladung, über die sie sich angemeldet hat. Käufe über Aufzeichnung, Anzeigen oder ohne Anmeldung fehlen hier.',
  },
  typischerTermin: {
    label: 'Typischer Termin',
    short: 'typisch',
    // B56: alle drei Gründe nennen, nicht nur die Messlücke — die Zahl der Show-up-Termine steht live in der Zeile.
    tip: 'Typisch = Median der Termine, die älter als 14 Tage sind, jede Spalte für sich. Keine echte Zeile. Termine ohne belastbare Teilnahme (Messlücke, Tracking-Warnung, übernommene Anmeldungen) zählen beim Show-up nicht mit.',
  },
  letzteFertigeTermine: {
    label: 'Die letzten 8 fertigen Termine',
    short: 'letzte 8 Termine',
    tip: 'Typischer Erwarteter Wert (Median) der letzten 8 Termine, die älter als 14 Tage sind, verglichen mit den übrigen fertigen Terminen der 12 Monate. Ein Pfeil erscheint nur, wenn der Unterschied größer ist als üblich.',
  },
  anteilWebinarAmWert: {
    label: 'Anteil Webinar am Erwarteten Wert',
    short: 'Anteil Webinar',
    tip: 'Anteil laut Kanal Webinar am Erwarteten Wert aller Anfos der 12 Monate.',
  },
  prognoseTermin: {
    label: 'Prognose',
    short: 'Prognose',
    einheit: 'Erwarteter Wert des Termins',
    tip: 'Was ein Termin dieses Webinars erwarten lässt, wenn es als Nächstes läuft: Erwarteter Wert aller Anfos des Termins, netto. Die mittlere Hälfte ist die Spanne, in der jeder zweite Termin landen sollte.',
  },
  testkandidat: {
    label: 'Testkandidat',
    short: 'Testkandidat',
    tip: 'Ab Platz 4 das wenig erprobte Webinar mit der besten oberen Spanne: höchstens 2 Termine mit fertigen Zahlen, oder ein Produkt mit höchstens 2 Webinaren. Einen Versuch wert, wenn man Neues lernen will. Sicher ist die Prognose dort nicht.',
    glossar: 'Testkandidat = wenig erprobtes Webinar mit Chance auf viel, einen Versuch wert',
  },
  letzteTermine: {
    label: 'Letzte Termine',
    short: 'Letzte Termine',
    tip: 'Erwarteter Wert der bisherigen Termine dieses Webinars, mit Datum und Kontakten.',
  },
  bilanzEmpfehlung: {
    label: 'Hat die Empfehlung gestimmt?',
    short: 'Bilanz',
    tip: 'Wie oft das Ergebnis eines Sonntags in der mittleren Hälfte der Prognose lag, und wie oft die Empfehlung gelaufen ist. Gezählt ab dem 01.11.2026, davor galt ein anderes Rechenverfahren. Sonntage, die bei der Empfehlung schon belegt waren, zählen bei „gelaufen“ nicht.',
  },
  webinareImFenster: {
    label: 'Webinare im 30-Tage-Fenster',
    short: 'Webinare im Fenster',
    tip: 'Wie viele Webinare in den 30 Tagen nach der Newsletter-Anmeldung liefen. Wenige Termine drücken die Quote.',
  },
  zahlenOffen: {
    // P1-V3 Regel 17: ersetzt „läuft nach"/„jung" an Terminen; die Karte hängt „bis TT.MM." an.
    label: 'Zahlen noch offen',
    short: 'Zahlen noch offen bis',
    tip: 'Der Termin ist jünger als 14 Tage: Es kommen noch Anfos dazu, Erwarteter Wert und Anfos sind deshalb grau. Anmeldungen und Show-up sind nach dem Termin fertig.',
    glossar: 'Zahlen noch offen = Termin jünger als 14 Tage, Anfos kommen noch dazu',
  },
  imVorjahrErstAb: {
    label: 'im Vorjahr erst ab',
    short: 'im Vorjahr erst ab',
    tip: 'Im Vorjahreszeitraum gab es dieses Produkt erst ab dem genannten Monat. Die Veränderung ist deshalb zum großen Teil Anlauf.',
  },
  keinWebinarMehr: {
    label: 'kein Webinar mehr',
    short: 'kein Webinar mehr',
    tip: 'Für dieses Produkt läuft kein Webinar mehr; sein Nachfolger steht daneben. Gepflegt als feste Liste, nicht aus der Zeit seit dem letzten Webinar abgeleitet.',
  },
  ueberWebinare: {
    label: 'Über Webinare',
    short: 'Über Webinare',
    tip: 'Anteil der Anfos des Produkts, die über ein Webinar kamen (Kanal Webinar). Zähler und Nenner ohne vorläufig ergänzte Anfos.',
  },
  letztesWebinar: {
    label: 'Letztes Webinar',
    short: 'Letztes Webinar',
    tip: 'Datum des letzten Webinars dieses Produkts, darunter wie viele Wochen das her ist.',
  },
  kaeuferUnterTeilnehmern: {
    label: 'Käufer unter den Teilnehmern',
    short: 'Käufer unter Teiln.',
    tip: 'Anteil der Teilnehmer, die eine Anfo abgegeben haben, x von y. Gezählt werden nur Anfos von Personen, die live dabei waren.',
  },
  // P1-Umbau 25.10.2026 (Bauer P1): weitere Begriffe der Karten und der Detailseite.
  anmeldungenLive: {
    label: 'Anmeldungen',
    short: 'Anmeldungen',
    tip: 'Personen mit Live-Anmeldung: vor Beginn angemeldet oder während des Webinars eingestiegen (nach Beginn direkt in den laufenden Raum). Eine Person zählt je Webinar einmal. Wer sich erst nach Beginn angemeldet hat und nicht mehr in den laufenden Raum kam (meist für die Aufzeichnung), steht getrennt als „zu spät für live“.',
  },
  showUpTermin: {
    label: 'Show-up',
    short: 'Show-up',
    einheit: 'der vor Beginn Angemeldeten',
    tip: 'Anteil der vor Beginn Angemeldeten, die nachweislich live dabei waren. Wer während des Webinars eingestiegen ist, zählt als Teilnehmer, aber nicht im Show-up. Teilnehmer sind eine Untergrenze. „—“, wenn die Teilnahmedaten des Termins nicht verlässlich sind (Messlücke, Tracking-Warnung oder übernommene Anmeldungen).',
  },
  teilnehmerImRaum: {
    label: 'Teilnehmer im Raum',
    short: 'Teilnehmer im Raum',
    tip: 'Personen, die der eigene Webinarraum live erfasst hat, einschließlich der während des Webinars Eingestiegenen. Die Zuschauerkurve rechnet mit dieser Zahl. „Teilnehmer“ der Kapitelseite kommt aus der Teilnahme-Messung; meist sind beide gleich. Hat der Raum weniger als die Hälfte der Teilnehmer erfasst oder mehr Personen das Angebot gesehen als Zuschauer im Raum waren, ist die Raum-Messung des Termins unvollständig — dann stehen hier keine Anteile.',
  },
  wertJe1kEinladung: {
    label: 'Erwarteter Wert je 1.000 Kontakte',
    short: 'je 1.000 Kontakte',
    einheit: 'nur Käufe über die Einladung',
    tip: 'Erwarteter Wert (bei Bestellung, netto) der Anfos, die über die Einladungs-Mails kamen, je 1.000 Kontakte. Gezählt wird je Person die erste Anfo über die Einladung, über die sie sich angemeldet hat. Käufe über Aufzeichnung, Anzeigen oder ohne Anmeldung fehlen; der Wert ist deshalb eine Untergrenze. Vorläufig ergänzte Anfos zählen 0 €.',
  },
  wertJeTerminTypisch: {
    label: 'Erwarteter Wert je Termin (typisch)',
    short: 'je Termin (typisch)',
    tip: 'Typischer Erwarteter Wert eines Termins dieses Produkts: Median der Termine der 12 Monate, die älter als 14 Tage sind.',
  },
  bestandskundenOhneProdukt: {
    label: 'Bestandskunden ohne dieses Produkt',
    short: 'Bestandskunden',
    tip: 'Personen mit einer gültigen Bestellung eines ANDEREN Produkts in den letzten 24 Monaten, die dieses Produkt nie bestellt haben — wen ein Webinar zu diesem Produkt zusätzlich erreichen kann. Geht nicht in die Prognose ein.',
  },
  pauseWochen: {
    label: 'Pause in Wochen',
    short: 'Pause (Wo.)',
    einheit: 'Webinar · Produkt · Thema',
    tip: 'Wochen seit dem letzten Termin dieses Webinars, des Produkts und des Themas bis zum Sonntag. Gesperrt wird, wenn das Produkt vor weniger als 3 Wochen, dasselbe Webinar vor weniger als 6 Wochen oder dasselbe Thema vor weniger als 3 Wochen lief.',
  },
  bezahlquoteTermine: {
    label: 'Bezahlquote der bisherigen Termine',
    short: 'Bezahlquote',
    tip: 'Anteil der Anfos der bisherigen Termine dieses Webinars mit einer Zahlung über 1 €, nur Anfos ab 60 Tagen. „offen“ = noch keine so alte Anfo. Trennt je Webinar kaum; bei Produkten mit Zahlung vorab ist sie hoch.',
  },
  chanceBesser: {
    label: 'Chance besser als Platz 1',
    short: 'Chance',
    tip: 'Wie wahrscheinlich dieser Kandidat einen höheren Erwarteten Wert hat als Platz 1, gemessen an der Unsicherheit beider Prognosen. Ab einem Viertel gelten die Plätze als gleichauf.',
  },
  gleichauf: {
    label: 'gleichauf',
    short: 'gleichauf',
    tip: 'Die Prognosen liegen so nah beieinander, dass der Vorsprung von Platz 1 nicht gesichert ist: Jeder dieser Kandidaten hat mindestens ein Viertel Chance, besser zu sein.',
  },
  warumKandidat: {
    label: 'Warum',
    short: 'Warum',
    einheit: '+ spricht dafür, − dagegen',
    tip: 'Die wichtigsten Gründe der Prognose: was den Wert hebt (+) oder drückt (−), bei Gesperrten der Grund und ab wann das Webinar wieder frei ist.',
  },
  vonHandPflegen: {
    label: 'Von Hand pflegen',
    short: 'Pflege',
    tip: 'Themen, Sperren und Anlässe wirken auf die Empfehlung: ein Thema sperrt dasselbe Thema für 3 Wochen, eine Sperre nimmt ein Produkt oder Webinar aus der Liste, ein Anlass erscheint als Hinweis. Zugeklappt, weil das Formular schreibt; die Kopfzeile sagt, ob etwas zu tun ist.',
  },
  upsellJeSeite: {
    label: 'Upsell-Quote',
    short: 'Upsell',
    tip: 'Anteil der Anfos, die gleich den Jahresbezug gewählt haben (Upsell), an allen Anfos der Zeile. Bei Produkten, die auch Seiten nur mit Jahresbezug haben, steht die Quote über die Seiten mit Wahl (Unterzeile „nur Seiten mit Wahl“) — die Spalte mischt also zwei Nenner und sortiert nach dem angezeigten Wert. Ohne vorläufig ergänzte Anfos.',
  },
  bestellungenVonBis: {
    label: 'Bestellungen',
    short: 'Bestellungen',
    einheit: 'erste–letzte Anfo',
    tip: 'Zeitraum zwischen der ersten und der letzten Anfo dieser Seite in den 12 Monaten.',
  },
  // Workshop P (29.10.2026): A2 Wert je Anmeldung, E1 nur FT, E3 Zeitraum, W2 Wiederholung.
  wertJeAnmeldung: {
    label: 'Erwarteter Wert je Anmeldung',
    short: '€ je Anmeldung',
    einheit: 'netto',
    tip: 'Erwarteter Wert (bei Bestellung, netto) aller Anfos, die den Terminen zugeordnet sind, geteilt durch ihre Live-Anmeldungen. Gezählt werden auch Käufe ohne Anmeldung (z. B. über die Aufzeichnung), wie in der Webinar-Tabelle. Vorläufig ergänzte Anfos zählen 0 €. Nur Termine, die mindestens 14 Tage alt sind.',
  },
  budgetJeAnmeldung: {
    label: 'Einkaufspreis je Anmeldung',
    short: 'Budget je Anmeldung',
    einheit: 'netto',
    tip: 'Was eine Anmeldung in der Akquise höchstens kosten darf: der vertretbare Einkaufspreis (CPA) der zugeordneten Anfos, geteilt durch die Live-Anmeldungen. Der CPA ist ein fester Anteil des Erwarteten Werts (Einstellung des Anfo-Wert-Modells). Steuerwert für den Einkauf von Anmeldungen, kein Zahlungseingang.',
  },
  anfosOhneAnmeldung: {
    label: 'Anfos ohne Anmeldung',
    short: 'ohne Anmeldung',
    tip: 'Anteil der zugeordneten Anfos (ohne vorläufige), deren E-Mail-Adresse für diesen Termin keine Live-Anmeldung hat — Käufe über die Aufzeichnung, mit einer anderen Adresse oder ohne Anmeldung. Sie stecken im Wert je Anmeldung mit drin.',
  },
  vorhersageUngeprueft: {
    label: 'Vorhersage, noch nicht geprüft',
    short: 'Vorhersage',
    tip: 'Der Erwartete Wert ist die Vorhersage des Anfo-Wert-Modells. Ob sie mit dem später gezahlten Geld übereinstimmt, ist noch nicht nachgeprüft (der Prüfstand steht aus). Euro-Werte je Anmeldung sind deshalb Richtwerte.',
  },
  nurFinanztrends: {
    label: 'nur Finanztrends',
    short: 'nur FT',
    tip: 'Nur die Versände an die Finanztrends-Liste: Kontakte, Anfos über die Einladung, vorläufige, Zahlungen, Abmeldungen und Versände kommen alle aus denselben FT-Versandzeilen; bei Direkt-Mailings nur die FT-Versände und die Anfos mit FT-Kennung. Gezählt werden dieselben Termine wie bei „alle Marken“. Die Obergrenze „mit allen Webinar-Käufen“ entfällt, weil sich die zugeordneten Webinar-Anfos nicht nach Marke trennen lassen.',
  },
  produkteZeitraum: {
    label: 'Zeitraum',
    short: 'Zeitraum',
    tip: '12 Monate: bis zum Stichtag, mit Veränderung zum Vorjahr. Seit 2025: ab 01.01.2025. Gesamt: ab der ersten Anfo (25.01.2024). Außerhalb von 12 Monaten fehlen die Veränderung zum Vorjahr und der typische Wert je Termin; Listenpreise älterer Anfos sind meist geschätzt, und die Kunden-Historie beginnt 2024.',
  },
  historieAb2024: {
    label: 'Historie ab 2024',
    short: 'Historie ab 2024',
    tip: 'Erstbesteller heißt: keine frühere Anfo derselben Adresse. Die Bestelldaten beginnen im Januar 2024 — wer 2024 bestellt hat, sieht deshalb fast immer wie ein Erstbesteller aus, auch wenn er früher schon Kunde war. Für 2024 ist der Anteil zu hoch.',
  },
  anmeldungenJe1kFt: {
    label: 'Anmeldungen je 1.000 FT-Kontakte',
    short: 'Anm. je 1.000 FT',
    tip: 'Personen mit Live-Anmeldung über die Finanztrends-Einladungen des Laufs, je 1.000 Kontakte dieser Einladungen (Versände an die ganze Liste nur mit den aktiven Empfängern, ohne Nachversand an Nicht-Öffner). Jede Person zählt einmal, bei der Einladung ihrer ersten Anmeldung.',
  },
  klickZuAnmeldung: {
    label: 'Klick → Anmeldung',
    short: 'Klick → Anm.',
    tip: 'Dieselben Personen geteilt durch die Klicker (eindeutige Klicks) der Finanztrends-Einladungen des Laufs. Misst, wie gut Einladung und Anmeldeseite zusammen aus einem Klick eine Anmeldung machen.',
  },
  laufNummer: {
    label: 'Lauf',
    short: 'Lauf',
    tip: 'Der wievielte Termin desselben Webinars das ist (gleiches Konzept-Kürzel oder gleicher öffentlicher Titel, wie in der Webinar-Tabelle). „≥“: der erste bekannte Termin liegt vor dem Beginn der Kontaktdaten (10.12.2025); davor tragen die Termine keine Kürzel, frühere Läufe mit anderem Titel können fehlen — die Zahl ist dann eine Untergrenze.',
  },
  pauseTage: {
    label: 'Pause',
    short: 'Pause',
    einheit: 'Tage seit dem vorigen Lauf',
    tip: 'Tage zwischen dem Start des vorigen Laufs desselben Webinars und diesem.',
  },
  // --- P2 ---(Kapitel „Umsatz & Bezahlqualität", P2-V3 § 3/§ 4.3/§ 4.4; spätere Zusätze des P2-Bauers NUR hier)
  laeuftNach6Monaten: {
    label: 'Läuft nach 6 Monaten noch',
    short: 'Läuft nach 6 Mon.',
    einheit: 'von allen Abo-Anfos',
    tip: 'Von 100 Abo-Anfos des Halbjahrs: wie viele bezahlt haben und 180 Tage nach der Bestellung noch laufen. Wer zum Laufzeitende gekündigt hat, zählt hier noch, wenn das Abo über Tag 180 hinaus läuft. Ohne Einmalkäufe und vorläufig ergänzte.',
  },
  kuendigungBinnen180: {
    label: 'Kündigung binnen 180 Tagen',
    short: 'Kündigung 180 T.',
    einheit: 'von den Abo-Zahlern',
    tip: 'Von den Abo-Anfos, die bezahlt haben: wie viele binnen 180 Tagen gekündigt haben. Zählt auch Kündigungen zum Laufzeitende, deren Abo noch über Tag 180 hinaus läuft; deshalb überschneidet sich die Zahl mit „Läuft nach 6 Monaten noch“. Misst etwas anderes als die Seite Kündigungsquoten (dort: Rechnung nicht bezahlt).',
  },
  bezahltJeZahler: {
    label: 'Bisher bezahlt je Zahler',
    short: 'bezahlt je Zahler',
    tip: 'Was eine Anfo mit Zahlung bis heute im Schnitt bezahlt hat, netto. Wächst mit dem Alter der Anfos und mischt Preismodelle — deshalb steht es nur zusammen mit dem Ø Alter.',
  },
  naechsteRechnungenSumme: {
    label: 'Nächste Rechnungen, Summe',
    short: 'Nächste Rechnungen',
    tip: 'Je laufendem Vertrag die nächste Rechnung in den 53 Wochen ab dieser Woche, netto, zusammengezählt. Eine Rechnung je Vertrag, nicht der Umsatz eines Jahres.',
  },
  voraussichtlichBezahlt: {
    label: 'Davon voraussichtlich bezahlt',
    short: 'voraussichtlich bezahlt',
    tip: 'Die nächsten Rechnungen, gewichtet damit, wie wahrscheinlich sie bezahlt werden: zahlende Verträge nach Mahnstufe, Verträge in der Testphase mit der erwarteten Wandlungsrate je Produkt — diese Annahme macht den größten Teil des Abschlags aus. Dieselbe Regel wie „bereinigt“ auf der Seite Cashflow, hier netto.',
  },
  zahlungseingangGegenPlan: {
    label: 'Zahlungseingang dieses Quartal gegen Plan',
    short: 'Eingang gegen Plan',
    tip: 'Echte Zahlungseingänge des laufenden Quartals gegen den zu Quartalsbeginn eingefrorenen Plan nach erwarteten Ausfällen, aufsummiert. Früh im Quartal schwankt der Wert stark. Euro-Beträge brutto auf der Seite Cashflow.',
  },
  laufendeVertraege: {
    label: 'Laufende Verträge mit nächster Rechnung',
    short: 'mit nächster Rechnung',
    tip: 'Laufende Verträge, deren nächste Rechnung in die 53 Wochen ab dieser Woche fällt.',
  },
  vertragLaeuftZahlt: {
    label: 'Läuft und zahlt',
    short: 'läuft, zahlt',
    tip: 'Vertrag läuft, und mindestens eine Rechnung über 1 € ist bezahlt.',
  },
  vertragGekuendigt: {
    label: 'Gekündigt, zahlt bis Ende',
    short: 'gekündigt',
    tip: 'Vertrag ist gekündigt, läuft aber bis zum Ende der bezahlten Laufzeit; bis dahin kann noch eine Rechnung kommen.',
  },
  vertragOhneZahlung: {
    label: 'Läuft, noch nichts gezahlt (Testphase oder offene Rechnung)',
    short: 'läuft, nichts gezahlt',
    tip: 'Vertrag läuft, aber noch keine Rechnung über 1 € ist bezahlt — meist noch in der Testphase oder mit offener Rechnung.',
  },
  neueLeads: {
    label: 'Neue Leads',
    short: 'Neue Leads',
    tip: 'Adressen, die sich zum ersten Mal für den Finanztrends-Newsletter angemeldet und bestätigt haben, bis zum Datenstand des Bestellsystems.',
  },
  wertJeNeuemLead: {
    label: 'Erwarteter Wert je neuem Lead',
    short: 'Erw. Wert je Lead',
    einheit: 'erste 90 Tage',
    tip: 'Was ein neuer Lead in seinen ersten 90 Tagen an Anfos erwarten lässt (Erwarteter Wert, netto). Für den Einkauf von Leads gilt die kleinere Zahl darunter: derselbe Anteil davon wie beim vertretbaren Einkaufspreis je Anfo der Produkte.',
  },
  einkaufspreisJeLead: {
    label: 'Vertretbarer Einkaufspreis je Lead',
    short: 'Einkaufspreis je Lead',
    tip: 'Was ein neuer Lead höchstens kosten darf: ein fester Anteil seines Erwarteten Werts der ersten 90 Tage, derselbe Anteil wie beim vertretbaren Einkaufspreis je Anfo. Netto.',
  },
  einkaufspreisJeAnfo: {
    // P2-V3: eigener Schlüssel — `cpaWertNetto` bleibt für die Alt-Leser.
    label: 'Vertretbarer Einkaufspreis je Anfo',
    short: 'Einkaufspreis je Anfo',
    tip: 'Was eine Anfo dieses Produkts in der Akquise höchstens kosten darf: ein fester Anteil ihres Erwarteten Werts, netto. Steuerwert für Kampagnen, kein Zahlungseingang.',
  },
  wertJeAnfoModell: {
    // P2-V3: eigener Schlüssel — `einzelwertNetto` liest P1.
    label: 'Erwarteter Wert je Anfo',
    short: 'Erw. Wert je Anfo',
    // B45 (26.10.2026): nur noch je ANGEBOT; der Wert je Produkt heißt sektionsweit „Einzelwert des Produkts“ (`einzelwertNetto`).
    tip: 'Was eine Anfo auf diesem Angebot über 36 Monate erwarten lässt, netto, nach dem heutigen Stand des Anfo-Wert-Modells — derselbe Wert, mit dem die Sektion jede Anfo dieses Angebots bewertet. Ein Mittel über alle Besteller, auch über die, die nie zahlen.',
  },
  widerrufsquote: {
    label: 'Widerrufsquote der Abo-Anfos',
    short: 'Widerrufsquote',
    tip: 'Anteil der Abo-Anfos, die widerrufen wurden (meist in den ersten 14 Tagen).',
  },
  offenerRechnungsbetrag: {
    label: 'Offener Rechnungsbetrag',
    short: 'offen',
    tip: 'Was auf gestellte Rechnungen noch fehlt, netto. Nur Stand heute, PL-X liefert keine Rechnungshistorie.',
  },
  anteilSchonBezahlt: {
    // P2-V3 § 4.2: ersetzt „Erreicht" (das klang nach Ziel); Text nach E8-Abgleich wie `erreicht.tip`.
    label: 'Anteil schon bezahlt',
    short: 'schon bezahlt',
    einheit: 'vom Erwarteten Wert',
    tip: 'Bisher bezahlt geteilt durch den heutigen Wert des Angebots derselben Anfos (beides netto). Ohne Anfos ohne Erwarteten Wert (Einmalkäufe ohne Angebotswert) und ohne vorläufig ergänzte Anfos, deren Zahlstand noch unbekannt ist. Junge Quartale haben noch wenig zahlen können; vergleiche deshalb nur ähnlich alte Quartale. Über 100 % in älteren Quartalen ist möglich: Der Wert ist aus den Zahlungen der letzten Monate gerechnet. 2025 haben mehr Besteller gezahlt als heute, 2024 haben Zahler zudem länger verlängert, und Anfang 2024 galten andere Preise.',
  },
  quartalspreisAngebot: {
    // `listenpreis` lesen auch P0/P1 — eigener Schlüssel, bis P1 dieselbe Netto-Regel im Dienst bekommt (P2-V3 #18).
    label: 'Ø Quartalspreis laut Angebot',
    short: 'Ø Quartalspreis',
    einheit: 'netto',
    tip: 'Mittlerer Quartalspreis laut Angebot zum Bestellzeitpunkt, netto. Nur Anfos, deren Preis zum Bestellzeitpunkt erfasst wurde; unter der Hälfte steht kein Wert.',
  },
  stufeBezahlt: {
    label: 'Bezahlt',
    short: 'Bezahlt',
    einheit: 'mehr als die Testphase',
    tip: 'Die Anfo hat mehr als die 0,99-€-Testphasenrechnung bezahlt (über 1 €).',
  },
  stufeNurTestphase: {
    label: 'Nur 0,99-€-Testphase bezahlt',
    short: 'Nur Testphase',
    tip: 'Bezahlt ist nur die Testphasenrechnung bis 1 €; die erste echte Rechnung nicht.',
  },
  stufeRechnungOffen: {
    label: 'Rechnung bekommen, nichts gezahlt',
    short: 'Rechnung, nichts gezahlt',
    tip: 'PL-X hat eine Rechnung gestellt, darauf ist nichts bezahlt.',
  },
  stufeRechnungKommt: {
    label: 'Rechnung kommt noch (Vertrag läuft)',
    short: 'Rechnung kommt noch',
    tip: 'Noch keine Rechnung, der Vertrag läuft — meist noch in der Testphase.',
  },
  stufeVorErsterRechnung: {
    label: 'Vor der ersten Rechnung beendet (Widerruf oder Kündigung)',
    short: 'Vor 1. Rechnung beendet',
    tip: 'Widerrufen oder beendet, bevor PL-X eine Rechnung gestellt hat.',
  },
  // P2-Bau 25.10.2026 (Stufe 1 P2): Begriffe der Karten, die Stufe 0 noch nicht angelegt hatte. Kein Satz nennt eine Zahl,
  // die sich ändert; Fenster, Schwellen und Datenstände hängen die Karten aus der API an.
  veraenderungVorjahreszeitraum: {
    // B51 (26.10.2026): derselbe Name wie `veraenderungVorjahr` in P1/P3 — gleiche Rechnung, gleiche Spanne ein Jahr früher.
    label: 'Veränderung zum Vorjahr',
    short: 'ggü. Vorjahr',
    einheit: 'gleiche Spanne, Anfos × Ø je Anfo',
    // Zeitbasis (E8): beide Zeiträume mit dem HEUTIGEN Angebotswert bewertet — die Veränderung vergleicht Menge und Mix.
    tip: 'Erwarteter Wert dieses Zeitraums gegen dieselbe Spanne ein Jahr früher, bis zum selben Tag und zur selben Uhrzeit. Gerechnet als Zahl der Anfos (ohne vorläufige) mal Ø Erwarteter Wert je Anfo. Anfos ohne Erwarteten Wert zählen als Anfo, nicht als 0 €. Beide Zeiträume sind mit dem heutigen Wert ihrer Angebote bewertet; die Veränderung zeigt also Menge und Angebotsmix, keinen Unterschied in der damaligen Bewertung.',
  },
  veraenderungVorjahresquartal: {
    label: 'Veränderung zum Vorjahresquartal',
    short: 'ggü. Vorjahresquartal',
    einheit: 'Anfos × Ø je Anfo',
    tip: 'Erwarteter Wert dieses Bestellquartals gegen dasselbe Quartal ein Jahr früher; das laufende Quartal bis zum selben Tag und zur selben Uhrzeit. Gerechnet als Zahl der Anfos (ohne vorläufige) mal Ø Erwarteter Wert je Anfo, beide Quartale mit dem heutigen Wert ihrer Angebote. Hängt nicht vom Alter ab und ist deshalb nie „jung“.',
  },
  anderesPreismodell: {
    label: 'anderes Preismodell',
    short: 'anderes Preismodell',
    tip: 'Im 1. Halbjahr 2024 galt ein anderes Preismodell mit deutlich niedrigerem Quartalspreis: Mehr Besteller zahlten, aber weniger. Siehe Karte „Preis, Bezahlung und Bindung je Halbjahr“.',
  },
  bezahlquoteDavor: {
    label: 'Bezahlquote ein Jahr früher',
    short: 'Quote davor',
    tip: 'Dieselbe Bezahlquote derselben Gruppe, ein Jahr früher: Anfos aus genau derselben Spanne des Vorjahres (bis 60 Tage vor dem Stichtag minus ein Jahr) — gleich lang, gleiche Monate. Die Lesezeile über der Tabelle vergleicht beide.',
  },
  zahlerAlleAlter: {
    // P5-Review (#11): „Zahler" hiess hier eine Anfo, im Hebel ein Kunde — die Unterzeile von „bezahlt" sagt jetzt „bezahlte Anfos".
    label: 'Bezahlte Anfos (jedes Alter)',
    short: 'bezahlte Anfos',
    tip: 'Anfos mit einer Zahlung über 1 €, egal wie alt — auch junge, deren Bezahlquote noch nicht gilt. Deshalb mehr als im „x von y“ der Bezahlquote, das nur reife Anfos zählt.',
  },
  reifeQuartale: {
    label: 'Reife Quartale',
    short: 'Reife Quartale',
    tip: 'Summe nur über die Quartale, die nicht mehr jung sind. Junge Quartale bekommen noch erste Rechnungen; mit ihnen läse man die Summe als zu niedrige Bezahlquote.',
  },
  anmeldequartal: {
    label: 'Anmeldequartal',
    short: 'Quartal',
    tip: 'Quartal, in dem sich der Lead zum ersten Mal für den Finanztrends-Newsletter angemeldet und bestätigt hat.',
  },
  anfoBinnen90: {
    label: 'Anfo binnen 90 Tagen',
    short: 'binnen 90 Tagen',
    tip: 'Anteil der neuen Leads, die binnen 90 Tagen nach ihrer Anmeldung eine Anfo abgegeben haben. Gezählt nur über Leads, die schon 90 Tage dabei sind — sonst wären junge Zeiträume per Konstruktion schlechter.',
  },
  anfoBinnen30: {
    label: 'Anfo binnen 30 Tagen',
    short: 'binnen 30 Tagen',
    tip: 'Anteil der neuen Leads, die binnen 30 Tagen nach ihrer Anmeldung eine Anfo abgegeben haben, gezählt über Leads, die schon 30 Tage dabei sind. Früher Hinweis, ob ein Zeitraum gut startet.',
  },
  wertAusLeads: {
    label: 'Erwarteter Wert aus diesen Leads',
    short: 'Erw. Wert (Summe)',
    einheit: 'erste 90 Tage, Summe',
    tip: 'Erwarteter Wert aller Anfos, die die Leads in ihren ersten 90 Tagen abgegeben haben, zusammengezählt; nur Leads, die schon 90 Tage dabei sind. Ø je Lead mal diese Leads.',
  },
  mitAnfoBisHeute: {
    label: 'Mit Anfo bis heute',
    short: 'mit Anfo',
    tip: 'Leads, die bis heute mindestens eine Anfo abgegeben haben, egal wann. Wächst mit dem Alter des Zeitraums.',
  },
  ersteAnfos: {
    label: 'Erste Anfos',
    short: 'Erste Anfos',
    tip: 'Leads mit mindestens einer Anfo, gezählt bei ihrer ersten Anfo.',
  },
  anteilErsteAnfos: {
    label: 'Anteil an allen ersten Anfos',
    short: 'Anteil',
    tip: 'Anteil dieser Zeile an allen ersten Anfos der neuen Leads im Zeitraum.',
  },
  wertErsteAnfo: {
    label: 'Ø Erwarteter Wert der ersten Anfo',
    short: 'Ø Erw. Wert',
    tip: 'Was die erste Anfo eines neuen Leads im Schnitt erwarten lässt, netto, mit dem heutigen Wert ihres Angebots.',
  },
  ersteAnfoBezahlt: {
    label: 'Erste Anfo bezahlt',
    short: 'erste bezahlt',
    tip: 'Anteil der ersten Anfos mit einer Zahlung über 1 €. Gezählt nur über erste Anfos, die mindestens so alt sind wie die Reife der Bezahlquote.',
  },
  kanalErsteAnfo: {
    label: 'Kanal der ersten Anfo',
    short: 'Kanal',
    tip: 'Woher die erste Anfo eines neuen Leads kam, erkannt an der Kennung im Bestell-Link.',
  },
  anteilSofortzahlung: {
    // B05/B36 (26.10.2026): der Wert ist geglättet, nicht gemessen — Name und Tooltip sagen es.
    label: 'Anteil Sofortzahlung im Modell',
    short: 'Sofortzahlung (Modell)',
    tip: 'Mit diesem Anteil mischt das Modell den Wert bei Testphase und bei Sofortzahlung zum Einzelwert des Produkts. Grundlage sind die Anfos der letzten 365 Tage; bei wenigen Anfos wird der Anteil zum Schnitt aller Produkte gezogen. Darunter steht der gemessene Anteil.',
  },
  wertTestphase: {
    label: 'Wert bei Testphase',
    short: 'bei Testphase',
    tip: 'Erwarteter Wert einer Anfo dieses Produkts mit Testphase, netto.',
  },
  wertSofortzahlung: {
    label: 'Wert bei Sofortzahlung',
    short: 'bei Sofortzahlung',
    tip: 'Erwarteter Wert einer Anfo dieses Produkts mit Sofortzahlung, netto.',
  },
  anfosInMessung: {
    label: 'Anfos in der Messung',
    short: 'in der Messung',
    tip: 'Anfos, deren Zahlung bis Tag 75 schon feststeht und die das Modell für diesen Wert gezählt hat. Gilt für die ganze Gruppe (Produkt und Zahlungsart), nicht je Angebot.',
  },
  bisTag75: {
    label: 'Ø bis Tag 75',
    short: 'bis Tag 75',
    tip: 'Was die gezählten Anfos im Schnitt in ihren ersten 75 Tagen gezahlt haben, mit dem Startwert gemischt, netto. Grundlage, die das Modell auf 36 Monate hochrechnet.',
  },
  startwert: {
    label: 'Startwert',
    short: 'Startwert',
    tip: 'Wert, mit dem das Modell beginnt, solange eigene Zahlungen fehlen: zählt so viel wie eine feste Zahl gezählter Anfos. Netto.',
  },
  erstbetrag: {
    label: 'Erstbetrag',
    short: 'Erstbetrag',
    tip: 'Erster Rechnungsbetrag laut Angebot (beim Jahresbezug das Vierfache des Quartalspreises), netto.',
  },
  aktionscode: {
    label: 'Angebot (Aktionscode)',
    short: 'Aktionscode',
    tip: 'Kennung der Werbeaktion, über die bestellt wird. Je Aktionscode ein Preis und eine Zahlungsart.',
  },
  wertJeErstbetrag: {
    label: 'Wert ÷ Erstbetrag',
    short: 'Wert ÷ Erstbetrag',
    tip: 'Erwarteter Wert je Anfo geteilt durch den ersten Rechnungsbetrag. Unter 1 heißt: Der Wert mittelt über alle Besteller, auch über die, die nie zahlen.',
  },
  anfosMitHochrechnung: {
    label: 'Anfos mit Hochrechnung',
    short: 'mit Hochrechnung',
    tip: 'Anfos, deren eigener Wert schon aus ihren Zahlungen bis Tag 75 hochgerechnet ist.',
  },
  nieBezahltWert0: {
    label: 'Nie bezahlt (Wert 0 €)',
    short: 'nie bezahlt',
    tip: 'Davon Anfos ohne Zahlung bis Tag 75: Ihre Hochrechnung ist 0 €.',
  },
  // --- Paket M (Workshop-Lücken 29.10.2026): Bindungskurve und Prüfstand auf der Anfo-Wert-Seite ---
  bindungModell: {
    label: 'Bindung laut Modell',
    short: 'Modell',
    tip: 'Anteil der Zahler, die diese Rechnung noch bezahlen, wie ihn der wirksame Lauf des Anfo-Werts annimmt. Mit dieser Kurve rechnet das Modell die Zahlung bis Tag 75 auf 36 Monate hoch.',
  },
  bindungGemessen: {
    label: 'Bindung gemessen',
    short: 'gemessen',
    tip: 'Anteil der Zahler, die diese Rechnung bezahlt haben, gemessen mit dem heutigen Stand über dieselben Zahler wie das Modell (letzte 24 Monate). Wer für die Rechnung noch nicht alt genug ist, zählt nur bis dahin mit (Kaplan-Meier). Weicht vom Modell auch ab, weil der Lauf mit dem Stand seines Tages gerechnet hat.',
  },
  imRisiko: {
    label: 'Zahler im Risiko',
    short: 'im Risiko',
    tip: 'Zahler, bei denen schon feststeht, ob sie diese Rechnung bezahlt haben: wer sie bezahlt oder nach der vorigen gekündigt hat. Unter 30 ist der Messpunkt grau.',
  },
  rechnungRechnerisch: {
    label: 'Rechnung (rechnerisch)',
    short: 'Rechnung',
    tip: 'Nummer der bezahlten Rechnung, gerechnet aus bezahltem Betrag ÷ Betrag je Rechnung. PL-X liefert keine Rechnungshistorie; diese Zählung gilt nur für die Modellprüfung, nicht als Kennzahl.',
  },
  pruefstand: {
    label: 'Prüfstand des Anfo-Werts',
    short: 'Prüfstand',
    tip: 'Vergleich der Vorhersage des Anfo-Werts mit dem, was dieselben Anfos bis zu einem Alter tatsächlich gezahlt haben. Belastbar nur mit Vorhersagen, die damals gespeichert wurden.',
  },
  // --- /Paket M ---
  // --- P4 --- (Kapitel „Hebel", Plan 2026-10-27-p4-hebel-p5-kern Paket A; spätere Zusätze des P4-Bauers NUR in diesem Block)
  welle: {
    label: 'Welle',
    short: 'Welle',
    tip: 'Ein Versand-Zeitfenster der Heat-Ups zu einem Webinar: Vortag, Webinartag früh (vor 12 Uhr) oder Webinartag spät (ab 12 Uhr). Alle Listen (Finanztrends, Schwestermarken, Partnerlisten) und beide Betreff-Varianten dieses Fensters zählen zusammen, auch wenn eine Liste zu einer anderen Uhrzeit verschickt. Soll sind drei Wellen je Webinar. Redlinks im Newsletter sind keine Welle.',
    glossar: 'Welle = alle Heat-Ups eines Zeitfensters zu einem Webinar (Vortag, Webinartag früh, Webinartag spät)',
  },
  welleFehlt: {
    label: 'Welle fehlt',
    short: 'fehlt',
    tip: 'In diesem Zeitfenster ging zu dem Webinar kein Heat-Up raus. Das ist keine Null: die Welle fand nicht statt.',
  },
  wellenKontakte: {
    label: 'Kontakte',
    short: 'Kontakte',
    tip: 'Empfänger der Heat-Ups dieser Welle über alle Listen. Versände an die ganze Liste (inklusive Inaktive) zählen nur mit ihren aktiven Empfängern. Ein Empfänger mehrerer Wellen zählt in jeder Welle — es sind Kontakte, keine Personen.',
  },
  wellenAnmeldungen: {
    label: 'Anmeldungen (erste Anmeldung)',
    short: 'Anmeldungen',
    tip: 'Personen mit Live-Anmeldung (vor Beginn oder während des Webinars eingestiegen), gezählt bei der Welle ihrer ERSTEN Anmeldung über eine Einladungs-Mail. Wer sich über zwei Wellen anmeldet, zählt nur bei der früheren.',
  },
  wellenTeilnehmer: {
    label: 'Teilnehmer',
    short: 'Teilnehmer',
    tip: 'Davon nachweislich live im Webinar, bei derselben Welle wie ihre Anmeldung. Bei Webinaren mit bekannter Messlücke oder Tracking-Warnung steht „—“, und sie fehlen im Wellen-Vergleich in Zähler und Nenner.',
  },
  wellenAnfos: {
    label: 'Anfos (erster Kontakt)',
    short: 'Anfos',
    tip: 'Die erste Anfo einer Person zum Webinar, gebucht auf die Welle ihrer ersten Anmeldung — jede Anfo genau einmal. Frühe Wellen sammeln die Interessierten zuerst ein; eine spätere Welle mit weniger Anfos ist deshalb nicht überflüssig. Vorläufig ergänzte Anfos zählen mit, aber nicht in „je 1.000“.',
  },
  wellenAbmeldungen: {
    label: 'Abmeldungen',
    short: 'Abm.',
    tip: 'Abmeldungen über die Heat-Ups dieser Welle. Bei Versänden an die ganze Liste zählen alle Abmeldungen, die Kontakte nur die aktiven Empfänger.',
  },
  wellenJe1000: {
    label: 'je 1.000 Kontakte',
    short: 'je 1.000',
    tip: 'Anzahl geteilt durch die Kontakte der Welle, mal 1.000. Anfos ohne vorläufig ergänzte.',
  },
  // Workshop-Lücke N2 (Plan 2026-10-29): Matrix Publisher-Liste × Welle.
  matrixListe: {
    label: 'Liste',
    short: 'Liste',
    tip: 'Die Liste, an die der Heat-Up ging. Börse-Express, Börse-Global, Stock-World und Trading-Treff stehen zusammen als „Schwestermarken“; jede andere Liste für sich.',
  },
  matrixIndex: {
    label: 'Index (Zeilenschnitt = 100)',
    short: 'Index',
    tip: 'Anmeldungen je 1.000 dieser Welle geteilt durch den Schnitt derselben Liste über alle ihre Wellen (Σ Anmeldungen ÷ Σ Kontakte), mal 100. Über 100 = diese Welle zieht bei dieser Liste besser als ihr Schnitt. Nur innerhalb einer Zeile vergleichen — die Listen sind verschieden groß und verschieden aktiv.',
  },
  matrixZeilenschnitt: {
    label: 'Schnitt der Liste',
    short: 'Schnitt',
    tip: 'Alle Wellen dieser Liste zusammen: Anmeldungen je 1.000 Kontakte, darunter Erwarteter Wert und Abmeldungen je 1.000.',
  },
  nachfassAnmelder: {
    label: 'Anmelder',
    short: 'Anmelder',
    tip: 'Personen mit Live-Anmeldung zu den Webinaren des Zeitraums, je Webinar einmal. Der Publisher kommt aus der Kennung ihrer ERSTEN Anmeldung zu diesem Webinar — dieselbe Regel, nach der der Nachfass den Bestell-Link kennzeichnet.',
  },
  nachfassAbdeckung: {
    label: 'Im Nachfass (Abdeckung)',
    short: 'im Nachfass',
    tip: 'Anmelder, die im verschickten Nachfass ihres Webinars standen, und ihr Anteil an allen Anmeldern. Wer schon gekauft hatte, bekommt bewusst keinen Nachfass; Webinare ganz ohne Nachfass drücken den Anteil — die Spalte „in Webinaren mit Nachfass“ rechnet nur über die Webinare, zu denen einer verschickt wurde.',
  },
  nachfassAbdeckungMitNachfass: {
    label: 'Abdeckung in Webinaren mit Nachfass',
    short: 'in Webinaren mit Nachfass',
    tip: 'Wie „Im Nachfass“, aber nur über die Webinare, zu denen ein Nachfass verschickt wurde. Der Rest zu 100 % sind vor allem Käufer, die keinen Nachfass bekommen sollen.',
  },
  nachfassEmpfaenger: {
    label: 'Empfänger',
    short: 'Empfänger',
    tip: 'Adressen in den verschickten Nachfass-Segmenten (Versand im Zeitraum), beide Gruppen (nicht dabei / dabei ohne Kauf) zusammen. Stand der Liste ist der letzte Abgleich vor der zweiten Mail: wer nach der ersten Mail gekauft hat, ist darin schon nicht mehr enthalten. Dieselbe Zahl wie die Kontakte der Nachfass-Zeile in „Direkte Bestell-Links“.',
  },
  nachfassAnfos: {
    label: 'Anfos über den Nachfass-Link',
    short: 'Anfos',
    tip: 'Anfos, deren Bestell-Link die Nachfass-Kennung trägt (Bestellung im Zeitraum), beim Publisher aus dieser Kennung. Dieselbe Zuordnung wie die Nachfass-Zeile in „Direkte Bestell-Links“; Käufe nach dem Webinar über andere Wege zählen hier nicht.',
  },
  nachfassAnfosJe1000: {
    label: 'Anfos je 1.000 Empfänger',
    short: 'je 1.000',
    tip: 'Anfos über den Nachfass-Link (ohne vorläufig ergänzte) je 1.000 Empfänger. Die Empfänger sind Angemeldete, keine kalte Liste — mit Heat-Ups oder Direkt-Mailings nicht vergleichbar.',
  },
  webinarAbdeckungNachfass: {
    label: 'Webinare mit Nachfass',
    short: 'Webinare mit Nachfass',
    tip: 'Webinare des Zeitraums (Start im Zeitraum, mit Anmeldungen), zu denen mindestens eine Nachfass-Gruppe verschickt wurde, geteilt durch alle Webinare des Zeitraums.',
  },
  publisher: {
    label: 'Publisher',
    short: 'Publisher',
    tip: 'Die Liste, über die die Person kam, erkannt am Kürzel vorn in der Kennung (FT, KME, BE, BG, SW, TT, DNV …). Ohne erkennbares Kürzel zählt sie zu Finanztrends — wie beim Versand des Nachfass.',
  },
  kundenNachBestellungen: {
    // P5-Review (#8): gezählt werden Anfos (ohne verweigerte) — „Bestellungen" bleibt für „inkl. verweigerter" reserviert.
    label: 'Kunden nach Anfos',
    short: 'Kunden',
    tip: 'Jede Adresse ist ein Kunde (ohne E-Mail: jede Anfo ein eigener Kunde). Gezählt sind alle Anfos seit Beginn der Daten; verweigerte Bestellungen zählen nicht. Eine Doppelbestellung (gleiches Produkt binnen 7 Tagen) und eine Jahresbezug-Order direkt neben einer anderen Anfo (binnen 2 Stunden, derselbe Kauf) zählen nicht als eigene Anfo. Vorläufig ergänzte Anfos fehlen (ohne Adresse).',
  },
  kundenZahler: {
    // P5-Review (#11): Kunden, nicht Anfos — anderswo heißt die Anfo-Zählung „bezahlte Anfos".
    label: 'Zahlende Kunden',
    short: 'zahlende Kunden',
    tip: 'Kunden mit mindestens einer bezahlten Anfo (mehr als 1 € gezahlt), nach heutigem Stand. Der Anteil darunter gilt innerhalb der Zeile: zahlende Kunden ÷ Kunden der Zeile.',
  },
  kundenUmsatz: {
    label: 'Bisher bezahlt',
    short: 'Bisher bezahlt',
    tip: 'Was alle Anfos dieser Kunden bis heute tatsächlich gezahlt haben, netto, Gutschriften abgezogen — echtes Geld, kein Schätzwert. Der Anteil ist der Teil am bezahlten Umsatz aller Kunden.',
  },
  kundenUmsatzJeKunde: {
    label: 'Ø bezahlt je Kunde',
    short: 'Ø je Kunde',
    tip: 'Bisher bezahlt geteilt durch die Kunden der Zeile, netto. Wächst mit dem Alter der Kunden — Mehrfachbesteller sind im Schnitt länger dabei (Spalte daneben).',
  },
  kundenTageSeitErster: {
    label: 'Ø Tage seit der ersten Anfo',
    short: 'Ø Tage dabei',
    tip: 'Wie lange die erste Anfo der Kunden im Schnitt zurückliegt. Wer länger dabei ist, hatte mehr Zeit für weitere Anfos und Zahlungen.',
  },
  kundenMehrfach: {
    label: 'Kunden mit mindestens zwei Anfos',
    short: '≥ 2 Anfos',
    tip: 'Anteil der Kunden mit zwei oder mehr gezählten Anfos, darunter ihr Anteil am bisher bezahlten Umsatz (netto).',
  },
  bestellungenJeKunde: {
    label: 'Anfos je Kunde',
    short: 'Anfos',
    tip: 'Zahl der gezählten Anfos einer Adresse seit Beginn der Daten (ohne Doppelbestellungen; verweigerte Bestellungen zählen nie).',
  },
  wellenVersand: {
    label: 'Versand',
    short: 'Versand',
    tip: 'Versandzeit (Berliner Zeit). „nur …“ bzw. „ohne …“: zu dieser Zeit verschickten nur diese Listen bzw. alle außer diesen. „ganze Liste“: Versand inklusive Inaktive.',
  },
  wellenWebinarWelle: {
    label: 'Webinar / Welle',
    short: 'Webinar / Welle',
    tip: 'Je Webinar die drei Wellen, darunter Versände zum Webinar, die keine Welle sind (Redlink-Mailing, Nachversand an Nicht-Öffner).',
  },
  kundenMehrprodukt: {
    label: 'Kunden mit mindestens zwei Produkten',
    short: '≥ 2 Produkte',
    tip: 'Kunden, deren gezählte Anfos mindestens zwei verschiedene Produkte (PL-X-Artikel) betreffen. Zwei Anfos desselben Produkts zählen hier als eines.',
  },
  // --- P5 --- (Seiten „Bestellungen" und „Werkzeuge", Plan 2026-10-27-p4-hebel-p5-kern Paket B; Zusätze des P5-Bauers NUR hier)
  bestellZeitraum: {
    label: 'Zeitraum',
    short: 'Zeitraum',
    tip: 'Bestelltag von–bis, beide Tage eingeschlossen (Berliner Zeit). Gezählt wird jede Bestellung, die in diesem Zeitraum einging.',
  },
  bestellungenAlle: {
    label: 'Bestellungen',
    short: 'Bestellungen',
    // V = Verweigert (plx-system.md § Status): PL-X hat die Bestellung nicht angenommen.
    tip: 'Alle Bestellungen einschließlich der von PL-X verweigerten (Status V) — so zählte die alte Anfo-Übersicht. Ohne stornierte Platzhalter und interne Testbestellungen. Bestellungen = Anfos + verweigert. Alle Quoten außer „Anteil verweigert“ rechnen mit den Anfos, nicht mit den Bestellungen.',
    glossar: 'Bestellungen = alle Bestellungen inklusive der von PL-X verweigerten = Anfos + verweigert',
  },
  verweigert: {
    label: 'Davon verweigert',
    short: 'verweigert',
    tip: 'Bestellungen, die PL-X nicht angenommen hat (Status V „Verweigert“). Den Grund je Bestellung meldet nur PL-X (PX-Data, „Vertragsbestätigungsanalyse“, hier nicht eingelesen): meist hatte die Adresse schon bestellt (Mehrfachbestellung, Mindestabstand), seltener Bonität oder eine manuelle Ablehnung. Sie zählen nicht als Anfo und in keiner Quote außer „Anteil verweigert“.',
    glossar: 'verweigert = PL-X hat die Bestellung nicht angenommen; sie ist keine Anfo, den Grund meldet nur PL-X',
  },
  verweigertAnteil: {
    label: 'Anteil verweigert (an Bestellungen)',
    short: 'verweigert %',
    tip: 'Verweigerte Bestellungen ÷ alle Bestellungen der Zeile × 100. Nenner sind die Bestellungen einschließlich der verweigerten — nicht die Anfos.',
  },
  zahlerBisher: {
    label: 'Bezahlte Anfos bisher',
    short: 'bezahlte Anfos',
    tip: 'Anfos mit einer Zahlung über 1 € bis heute, egal wie alt. Junge Anfos sind meist noch in der Testphase — in einem frischen Zeitraum ist die Zahl deshalb klein, ohne dass etwas schlecht lief. Vorläufig ergänzte zählen nicht.',
  },
  alterAnfos: {
    label: 'Ø Alter',
    short: 'Ø Alter',
    tip: 'Wie alt die Anfos der Zeile heute im Schnitt sind, in Tagen. „Bisher bezahlt“ wächst mit dem Alter — Zeilen verschiedenen Alters über den Erwarteten Wert vergleichen, nicht über „bezahlt“.',
  },
  wertJeAnfoP5: {
    label: 'Ø Erwarteter Wert je Anfo',
    short: 'Ø je Anfo',
    tip: 'Erwarteter Wert (bei Bestellung) je Anfo, netto, über die Anfos mit Wert. Vorläufig ergänzte und Anfos ohne Wert zählen nicht mit.',
  },
  produktDerAnfo: {
    label: 'Produkt',
    short: 'Produkt',
    tip: 'Das bestellte Produkt (Artikel laut PL-X), darunter das Kürzel.',
  },
  werbeaktion: {
    label: 'Werbeaktion (WKZ)',
    short: 'WKZ',
    tip: 'Werbekennzeichen der Bestellung — legt Produkt, Preis und Bezugsart fest. Darunter das Produkt und, wo so konfiguriert, „Jahresbezug“ oder „Einmalkauf“.',
  },
  kanalDerAnfo: {
    label: 'Kanal',
    short: 'Kanal',
    tip: 'Woher die Bestellung kam, erkannt an der Kennung (Banner) im Bestell-Link — dieselbe Zuordnung wie im Kapitel „Kanäle & Werbeflächen“. Darunter die Kanalgruppe.',
  },
  bezahltNettoListe: {
    label: 'Bisher bezahlt (netto)',
    short: 'netto',
    tip: 'Was diese Anfo bis heute gezahlt hat, netto (ohne MwSt.), Gutschriften abgezogen — dieselbe Zahl wie „Bisher bezahlt“ in den Übersichten oben. Leer bei Bestellungen, die keine Anfo sind (verweigert, Testbestellung). Die Spalte „Brutto“ daneben enthält die MwSt.',
  },
  // --- P5-Review Daten ---
  umsatzBruttoListe: {
    label: 'Umsatz brutto',
    short: 'brutto',
    tip: 'Was die Bestellungen der Auswahl bis heute tatsächlich bezahlt haben, brutto (inkl. MwSt.), Gutschriften abgezogen — die Spalte „Brutto“ der Liste. Die Übersichten oben rechnen netto.',
  },
  bezahltNettoListeSumme: {
    label: 'Bisher bezahlt (netto)',
    short: 'netto',
    tip: 'Summe der Netto-Spalte über die Auswahl, ohne vorläufig ergänzte Anfos — so rechnen auch die Übersichten oben („Bisher bezahlt“). Eine vorläufige Anfo zeigt ihren Betrag in der Zeile, markiert „vorläufig“.',
  },
  letzteKw: {
    label: 'letzte KW',
    short: 'letzte KW',
    tip: 'Die vorige volle Kalenderwoche, Montag bis Sonntag — dieselben Wochen wie die Karte „Verweigerte Bestellungen“.',
  },
  datenBis: {
    label: 'Daten bis',
    short: 'Daten bis',
    tip: 'Jüngste Bestellung, die PL-X bisher geliefert hat (Berliner Zeit). Danach liegt noch nichts vor — ein Zeitraum bis heute ist am letzten Tag noch unvollständig.',
  },
  // --- Ende P5-Review Daten ---
  verweigertSchonBestellt: {
    label: 'davon Adresse hatte schon bestellt',
    short: 'schon bestellt',
    tip: 'Verweigerte Bestellungen, deren E-Mail-Adresse in den 90 Tagen davor schon bei PL-X bestellt hatte. Spricht für Mehrfachbestellung oder Mindestabstand, die häufigsten Gründe bei PL-X — ist aber kein sicherer Grund: auch eine Ablehnung wegen Bonität kann eine frühere Bestellung haben. Den Grund je Bestellung meldet nur PL-X.',
  },
  werkzeuge: {
    label: 'Werkzeuge',
    short: 'Werkzeuge',
    tip: 'Prüf- und Nachschlageseiten zu den Webinar-Zahlen: welche Bestellung welchem Webinar zugeordnet ist, wie Anmelde-Kennungen gruppiert werden, der Design-Test der Anmeldeseiten und die Formeln der Webinar-Kennzahlen.',
  },
  // --- P5-Review Darstellung --- (Review-Runde 27.10.2026, Bauer V: Verständlichkeit und Darstellung; Zusätze NUR hier)
  wertUnterBezahlt: {
    label: 'Erwarteter Wert unter „bisher bezahlt“',
    short: 'Jahresbezug',
    tip: 'Beim Jahresbezug zahlt ein Zahler sofort mehr als dieser Durchschnitt, in den auch die Nichtzahler eingehen. „Bisher bezahlt“ kann deshalb über dem Erwarteten Wert liegen, ohne dass einer der beiden falsch ist.',
  },
  wellenUeberHeatUp: {
    label: 'über Heat-Up-Mails',
    short: 'über Heat-Ups',
    tip: 'In der Webinarzeile nur, was über die drei Heat-Up-Wellen kam (erster Kontakt je Versand); Redlink-Mailing und Nachversand stehen als eigene Zeilen darunter. Die Detailseite des Webinars zählt alle Anmeldungen, Teilnehmer und Anfos, auch über Newsletter, Bestätigungsseite, Werbeflächen oder ganz ohne Anmeldung — dort stehen deshalb größere Zahlen.',
  },
  befund: {
    label: 'Befund',
    short: 'Befund',
    tip: 'Was die Zahlen dieser Karte zeigen, beim Laden aus den Daten gerechnet. Beschreibend: er nennt keine Ursache. Wo er einen Test vorschlägt, ist das ein Kandidat, kein Beweis.',
  },
  verweigertBefund: {
    label: 'Befund verweigerte Bestellungen',
    short: 'Befund',
    tip: '„Hatte schon bestellt“ ist ein Hinweis, kein Grund: auch eine Ablehnung wegen Bonität kann eine frühere Bestellung haben. Nur Bonitätsfälle kommen für ein anderes Angebot (z. B. Vorkasse) in Frage; sie liegen am ehesten bei den Verweigerten ohne frühere Bestellung.',
  },
  // --- Persona-Umbau 28.10.2026, Teil A ---
  // Hebel (P4) und Bestellungen (P5), Plan 2026-10-28-persona-umbau-paket-1-2 § A. Zusätze des Builders A NUR hier.
  nachfassWertJe1000: {
    label: 'Erwarteter Wert je 1.000 Empfänger',
    short: '€ je 1.000 Empf.',
    tip: 'Erwarteter Wert (bei Bestellung, netto) der Anfos über den Nachfass-Link je 1.000 Empfänger — dieselbe Rechnung wie die Nachfass-Zeile in „Direkte Bestell-Links“ (Kapitel Kanäle). Eine Erwartung über 36 Monate, kein Umsatz bis heute. Die Empfänger sind Angemeldete, keine kalte Liste.',
  },
  nachfassUebrige: {
    label: 'übrige Listen',
    short: 'übrige Listen',
    tip: 'Publisher mit zu wenigen Anfos für eine eigene Quote, zusammengefasst; die einzelnen Listen stehen grau darunter — ihre Quoten hängen an ein, zwei Anfos.',
  },
  nachfassOhne: {
    label: 'Webinare ohne Nachfass',
    short: 'ohne Nachfass',
    tip: 'Webinare des Zeitraums (Start im Zeitraum, mit Anmeldungen), zu denen keine Nachfass-Gruppe verschickt wurde. Die Schätzung daneben: Ø Nachfass-Anfos je Webinar mit Nachfass × Zahl dieser Webinare, bewertet mit dem Ø Erwarteten Wert je Nachfass-Anfo — eine Schätzung, keine Messung.',
  },
  heatUpVergleich: {
    label: 'Heat-Up',
    short: 'Heat-Up',
    tip: 'Erwarteter Wert je 1.000 Kontakte der Heat-Ups am Vortag und am Webinartag zusammen — dieselbe Rechnung wie die Karte „Webinar-Einladungen“ im Kapitel Kanäle, gleiches Fenster. Heat-Ups gehen an die ganze Liste, der Nachfass nur an Angemeldete; der Abstand zeigt, was eine Mail an Angemeldete wert ist, nicht dass Heat-Ups verzichtbar wären.',
  },
  wellenWertJe1000: {
    label: 'Erwarteter Wert je 1.000 Kontakte',
    short: '€ je 1.000',
    tip: 'Anfos der Welle (erster Kontakt, ohne vorläufig ergänzte) × mittlerer Erwarteter Wert (bei Bestellung, netto) der Anfos ihres Webinars, je 1.000 Kontakte — dieselbe Bewertung wie die Heat-Up-Zeilen der Karte „Webinar-Einladungen“ im Kapitel Kanäle. Nur im Vergleich über alle Webinare; je Webinar und Welle sind es zu wenige Anfos für einen Euro-Wert.',
  },
  wellenAbmJeAnm: {
    label: 'Abmeldungen je Anmeldung',
    short: 'Abmeldungen je Anmeldung',
    tip: 'Abmeldungen über die Heat-Ups der Welle geteilt durch ihre Anmeldungen (erste Anmeldung): was eine Anmeldung an Liste kostet. Über 1 heißt: die Welle verliert mehr Empfänger, als sie Anmeldungen bringt.',
  },
  kundenMehrfachUmsatz: {
    label: 'Umsatzanteil der Kunden mit 2 und mehr Anfos',
    short: 'Umsatzanteil ≥ 2 Anfos',
    tip: 'Teil des bisher bezahlten Umsatzes (netto, echtes Geld), der von Kunden mit zwei oder mehr gezählten Anfos kommt; darunter, welcher Teil der Kunden das ist. Mehrfachkunden sind im Schnitt länger dabei — der Anteil ist zum Teil Alter, nicht nur Treue.',
  },
  bestellVergleich: {
    label: 'gegen Ø gleich langer Zeiträume davor',
    short: 'gegen Ø davor',
    tip: 'Veränderung gegen den Schnitt gleich langer Zeiträume in den 12 Wochen vor dem Start (bei 7 Tagen: die 12 Wochen davor; ist der Zeitraum länger als 12 Wochen: der gleich lange Zeitraum davor). Reicht der Zeitraum über den Datenstand hinaus, zählt jeder Vergleichszeitraum nur bis zur selben Stelle. ▲/▼ nur, wenn der Wert höher bzw. niedriger liegt als in jedem Vergleichszeitraum (ab 4 Zeiträumen) — sonst liegt er im üblichen Schwankungsbereich.',
  },
  verweigertOhneFruehere: {
    label: 'Verweigert ohne frühere Bestellung',
    short: 'ohne frühere Bestellung',
    tip: 'Verweigerte Bestellungen, deren Adresse in den 90 Tagen davor nicht bestellt hatte. Hier liegen am ehesten die Bonitäts- und manuellen Ablehnungen — die Fälle, in denen ein Kauf verloren gehen kann; die übrigen Verweigerten sind meist Mehrfachbestellungen. „je Woche“ ist der Schnitt der abgeschlossenen Wochen.',
  },
  bezahltSpalteAus: {
    label: 'Bisher bezahlt ausgeblendet',
    short: 'bezahlt ausgeblendet',
    tip: 'Junge Anfos sind meist noch in der Testphase — „bisher bezahlt“ ist dann fast null und sagt nichts. Die Spalte steht erst ab einem Ø Alter von 60 Tagen von selbst in den Tabellen; über „Spalten“ lässt sie sich immer einblenden. Der Excel-Export enthält sie immer.',
  },
  // --- Persona-Umbau 28.10.2026, Teil B ---
  gegenTypischVorTermin: {
    label: 'gegen typisch',
    short: 'gegen typisch',
    tip: '▲/▼: dieser Termin gegen den typischen Termin der 12 Monate VOR ihm (Median der Termine dieses Zeitraums, die älter als 14 Tage sind; Anfos bis heute gezählt; jede Größe für sich und erst ab 5 Terminen). Verglichen wird mit der Zeit vor dem Termin, nicht mit heute — die Kennzahlen ändern sich über die Zeit. Ein Pfeil ab 5 % Abstand, beim Show-up ab 2 Prozentpunkten; darunter „≈“. Bei einem Termin mit noch offenen Zahlen steht der Pfeil grau: Anfos und Erwarteter Wert wachsen noch.',
  },
  gegenTypisch: {
    label: 'gegen typisch',
    short: 'gegen typisch',
    tip: '▲/▼: dieser Termin gegen den Typischen Termin der Webinar-Tabelle (Median der Termine der letzten 12 Monate, die älter als 14 Tage sind, jede Größe für sich). Ein Pfeil ab 5 % Abstand, beim Show-up ab 2 Prozentpunkten; darunter „≈“. Bei einem Termin mit noch offenen Zahlen steht der Pfeil grau: Anfos und Erwarteter Wert wachsen noch.',
  },
  empfehlungKachel: {
    label: 'Empfehlung',
    short: 'Empfehlung',
    tip: 'Platz 1 der Karte „Was als Nächstes laufen sollte“ für den nächsten freien Sonntag, mit seiner Prognose (Erwarteter Wert des Termins, netto). Liegen mehrere Plätze innerhalb der Unsicherheit gleichauf, steht das daneben.',
  },
  anteilWertUeberWebinare: {
    label: 'des Erwarteten Werts kam über Webinare',
    short: 'über Webinare',
    tip: 'Anteil laut Kanal Webinar am Erwarteten Wert aller Anfos, Bestellungen der letzten 12 Monate.',
  },
  bestellseiteAusgelaufen: {
    label: 'Ausgelaufen',
    short: 'ausgelaufen',
    tip: 'Bestellseite ohne Anfo in den letzten 30 Tagen. Ihre Anfos zählen weiter beim Produkt; die Quote beschreibt, wie die Seite lief, nicht was sie heute bringt.',
  },
  nieGezahltAnteil: {
    label: 'Käufer „früher bestellt, nie gezahlt“',
    short: 'nie gezahlt',
    tip: 'Anteil der Käufer dieses Termins, die schon früher bestellt und bis heute nie gezahlt haben (je Person, erste Anfo des Termins). Vergleich: Anteil der Anfos dieses Kundentyps in der ganzen Sektion, letzte 12 Monate (Seite „Umsatz & Bezahlqualität“, Karte „Wer bestellt — und wer zahlt“) — dort je Anfo, hier je Käufer; eine Person mit zwei Anfos eines Termins ist selten.',
  },
  geraetNichtErfasst: {
    label: 'Gerät nicht erfasst',
    short: 'Gerät nicht erfasst',
    tip: 'Die Tabelle nach Anmeldegerät steht nur, wenn mindestens die Hälfte der Anmeldungen ein bekanntes Gerät hat; sonst wäre sie fast nur die Zeile „Unbekannt“.',
  },
  // --- Persona-Umbau 28.10.2026, Teil C ---
  // Plan 2026-10-28-persona-umbau-paket-1-2 § C (Umsatz & Bezahlqualität): Euro je Rechnungsstufe (C3).
  wertRechnungsstufe: {
    label: 'Erwarteter Wert dieser Anfos',
    short: 'Wert der Stufe',
    einheit: 'bei Bestellung, netto',
    tip: 'Der Erwartete Wert bei Bestellung (heutiger Angebotswert, 36 Monate, netto) der Anfos dieser Rechnungsstufe — was ihnen beim Bestellen zugeschrieben war. Eine Schätzung, kein entgangenes Geld: der Erwartete Wert rechnet schon ein, dass viele Anfos nie zahlen.',
  },
  groessterVerlust: {
    label: 'Größter Verlust',
    short: 'Größter Verlust',
    tip: 'Die Rechnungsstufe ohne Zahlung, in der über die reifen Quartale die meisten Anfos enden. Beim Laden aus den Daten bestimmt.',
  },
  // --- Workshop-Lücken 29.10.2026, Paket U --- (Plan 2026-10-29-workshop-luecken: W1, N1, A4, N3; Zusätze NUR hier)
  abbruchTag: {
    label: 'Tag des Endes',
    short: 'Tag des Endes',
    einheit: 'Tage nach der Bestellung',
    tip: 'Bei Anfos, die vor ihrer ersten Rechnung widerrufen oder gekündigt wurden: wie viele Tage nach der Bestellung der Vertrag laut PL-X endete (Kündigungsdatum). Gezählt sind genau die Anfos der Stufe „Vor der ersten Rechnung beendet“. Das Ende liegt meist, aber nicht immer in der Testphase.',
  },
  abbruchSpanne: {
    label: 'Mittlere Hälfte (25.–75. Perzentil)',
    short: 'mittlere Hälfte',
    tip: 'Die Hälfte dieser Anfos endete zwischen diesen beiden Tagen; ein Viertel davor, ein Viertel danach. Der Punkt ist der Median (die Hälfte endete früher, die Hälfte später), der Strich das 90. Perzentil (9 von 10 endeten bis dahin). Nur reife Quartale.',
  },
  endeTestphase: {
    label: 'Ende der Testphase',
    short: 'Ende Testphase',
    tip: 'Tag, an dem die Testphase endet und die erste Rechnung kommt. Die Linie steht nur, wenn in jedem gezeigten Quartal fast alle dieser Anfos (mindestens 95 %) dieselbe Testphasenlänge hatten; sonst nennt die Karte die Längen.',
  },
  verlustBilanz: {
    label: 'Verlust-Bilanz',
    short: 'Verlust-Bilanz',
    tip: 'Von allen Bestellungen eines Zeitraums bis zu den bezahlten Anfos: wie viele an welcher Stelle wegfallen. Jede Anfo steht in genau einer Stufe, die Stufen ergeben zusammen alle Anfos. Anteile über die Anfos mit bekanntem Zahlstand; verweigerte Bestellungen sind keine Anfos und stehen nur als Anzahl. Nur Anfos, die mindestens so alt sind wie die Reife — danach kommt kaum noch eine erste Zahlung. Euro steht nur oben (Erwarteter Wert aller Anfos) und unten (bisher bezahlt): der Erwartete Wert rechnet schon ein, dass viele Anfos nie zahlen, ein Euro-Betrag je Stufe wäre kein entgangenes Geld.',
  },
  geldNachAlter: {
    label: 'Bisher gezahlt nach Alter',
    short: 'gezahlt bis Tag h',
    einheit: 'je Anfo, netto',
    tip: 'Was die Anfos eines Bestellmonats bis zum Alter von 30, 60, 90 oder 180 Tagen tatsächlich gezahlt hatten, je Anfo, netto — Anfos ohne Zahlung zählen mit 0 €. Gemessen am täglich gespeicherten Zahlungsstand (seit 15.05.2026), nicht geschätzt. Rückbuchungen senken den Wert, die Zahl kann mit dem Alter deshalb auch fallen. Eine Zelle steht nur, wenn für ALLE Anfos des Monats der Stand an diesem Tag bekannt ist; sonst grau „bekannt von“. Netto über das Verhältnis brutto/netto der bezahlten Anfos des Produkts (der Zahlungsstand kennt nur brutto).',
  },
  cpaJeAnfoBestellung: {
    label: 'Vertretbarer Einkaufspreis je Anfo',
    short: 'Einkaufspreis je Anfo',
    einheit: 'bei Bestellung, netto',
    tip: 'Mittlerer CPA-Wert bei Bestellung der Anfos des Monats, netto: der Betrag, den eine Anfo in der Akquise höchstens kosten darf (Angebot, sonst Produkt). Ein Steuerwert aus dem Anfo-Wert-Modell, kein Aufwand und kein Zahlungseingang. Anfos ohne CPA-Wert zählen nicht als 0, sie sind herausgerechnet und werden genannt.',
  },
  cpaBudgetErreicht: {
    label: 'Einkaufspreis bezahlt ab Alter',
    short: 'erreicht ab',
    tip: 'Ab diesem Alter hatten die Anfos des Monats im Schnitt so viel gezahlt wie der vertretbare Einkaufspreis je Anfo („CPA-Budget erreicht“). „spätestens“: für ein jüngeres Alter ist der Zahlungsstand nicht vollständig bekannt. Der Einkaufspreis ist eine Vorhersage des Anfo-Wert-Modells und noch nicht gegen echte Zahlungen geprüft.',
  },
  qualitaetJeQuelle: {
    label: 'Bezahlquote je Quelle',
    short: 'Bezahlquote je Quelle',
    tip: 'Bezahlquote der Anfos einer Quelle, die 60 bis 240 Tage alt sind (also reif), gegen dieselben Tage ein Jahr früher. Bei Webinaren statt des Vorjahrs gegen den Rest ihres Produkts im selben Zeitraum ohne die Anfos dieses Webinars. Vorläufig ergänzte Anfos zählen nicht.',
  },
  produktRest: {
    label: 'Rest des Produkts',
    short: 'Produkt ohne dieses Webinar',
    tip: 'Bezahlquote aller übrigen reifen Anfos des Produkts im selben Zeitraum, aus allen Kanälen — ohne die Anfos dieses Webinars. Das Produkt ist das Etikett des Webinars; bei Webinaren mit mehreren Produkten zählen trotzdem alle ihre Anfos. Webinare ohne Produktetikett haben keinen Vergleich.',
  },
  // --- Persona-Umbau 28.10.2026, Teil D ---
  zuordnungAnmeldung: {
    label: 'Zuordnung über die Anmeldung',
    short: 'über Anmeldung',
    tip: 'Die erste Anfo einer Person je Webinar zählt für die Fläche, über die sie sich zuerst für dieses Webinar angemeldet hat — egal über welchen Link sie danach bestellt (Karte „Webinar-Einladungen“).',
  },
  zuordnungBestellLink: {
    label: 'Zuordnung über den Bestell-Link',
    short: 'über Bestell-Link',
    tip: 'Die Anfo zählt für die Fläche, deren Kennung im Bestell-Link steht (Karte „Direkte Bestell-Links“). Ein Kauf kann deshalb bei beiden Zuordnungen auftauchen; in der Größenordnung sind die Werte vergleichbar, in Abstufungen von wenigen Euro nicht.',
  },
  rangWerbeflaechen: {
    label: 'Rang der Werbeflächen',
    tip: 'Alle Flächen mit Kontaktzahl in einer Liste, nach Erwartetem Wert je 1.000 Kontakte. Die Werte stammen unverändert aus den Karten „Webinar-Einladungen“ und „Direkte Bestell-Links“. Der Nachfass geht nur an Angemeldete und steht deshalb außerhalb des Rangs.',
  },
  kernDmHeatUp: {
    label: 'Direkt-Mailings gegen Heat-Ups',
    tip: 'Direkt-Mailings = Finanztrends, Schwestermarken und Partnerlisten zusammen (Zuordnung über den Bestell-Link); Heat-Ups = Vortag und Webinartag zusammen (Zuordnung über die Anmeldung). Je Seite Summe des Erwarteten Werts ÷ Summe der Kontakte × 1.000 — dieselbe Rechnung wie je Fläche. Das Verhältnis taugt für die Größenordnung, nicht für wenige Euro.',
  },
  umbuchungMoeglich: {
    label: 'Kennung geändert',
    short: 'Kennung geändert',
    tip: 'In dieser Zeile haben sich die Kennungen in den Bestell-Links geändert. Ein Teil der Veränderung kann deshalb Umbuchung zwischen Kanälen sein, nicht das Geschäft. Die Summe aller Anfos betrifft das nicht.',
  },
  bezahlquoteGegenVergleich: {
    label: 'Bezahlquote gegen den Vergleichszeitraum',
    short: 'Pp',
    tip: 'Unterschied in Prozentpunkten (Pp). Gezählt werden nur Anfos, die heute mindestens 60 Tage alt sind; der Vergleich nimmt genau dieselbe Spanne, nur verschoben — gleich lang, gleiche Monate. Die Bezahlquote ändert sich über die Zeit, ein ▼ gegen das Vorjahr zeigt deshalb auch diesen Trend, nicht nur einen schlechten Zeitraum. ▲/▼ steht nur, wo ein Test über beide Zeiträume den Unterschied vom Zufall trennt — dieselbe Regel wie auf „Umsatz & Bezahlqualität“.',
  },
  direktMailingHaelften: {
    label: 'Direkt-Mailings: letzte 3 Monate gegen die 3 Monate davor',
    tip: 'Nach Versandtag, Versände ab 14 Tagen Alter; Anfos zählen zum Versand (Kennung im Bestell-Link). Die letzten 3 Monate sind genau die Direkt-Mailing-Zeilen der Karte „Direkte Bestell-Links“ bei 3 Monaten, beide zusammen die bei 6 Monaten. Gegen das Vorjahr geht das nicht: Kontakte je Versand gibt es erst seit 23.12.2025.',
  },
  teaserKernzahl: {
    label: 'Kernzahl',
    tip: 'Je Kapitel die Zahl, die seine Hauptfrage beantwortet, mit Richtung und Fenster — dieselbe Rechnung wie auf der Kapitelseite. Fehlt sie, konnte sie gerade nicht geladen werden.',
  },
} as const;

export type TermKey = keyof typeof TERMS;

/**
 * Begriffe des Blocks „Was wir zählen" auf der Übersicht (dieselbe Liste wie `ZAEHLREGELN` in `scenes/auswertungen/index.tsx`).
 * Nur auf diese darf ein Tooltip mit „siehe ‚Was wir zählen'" verweisen — sonst liefe der Verweis ins Leere.
 */
export const WAS_WIR_ZAEHLEN: readonly TermKey[] = [
  'anfos',
  'bestellungenAlle',
  'verweigert',
  'vorlaeufig',
  'umsatzNetto',
  'anfoWertNetto',
  'wertBeiBestellung',
  'hochrechnung',
  'bezahlquote',
  'reife',
  'rechnungsstufe',
  'erstbesteller',
  'frueherGezahlt',
  'frueherNieGezahlt',
  'seriellerNichtzahler',
  'upsellQuote',
  'listenpreis',
  'kanal',
];

/** Ab dieser Länge (Zeichen) wird ein Spalten-Tooltip auf den spaltenspezifischen Teil gekürzt (Audit C4, 30.10.2026). */
export const SPALTEN_TIP_MAX = 400;

/**
 * Spalten-Tooltip aus Begriff + spaltenspezifischem Teil (Audit C4). Bis `SPALTEN_TIP_MAX` Zeichen der ganze Text; darüber
 * — nur für Begriffe aus „Was wir zählen" — der spaltenspezifische Teil und „Begriff: siehe ‚Was wir zählen'". Für andere
 * Begriffe bleibt der ganze Text stehen (kein Verweis ins Leere).
 */
export function spaltenTip(begriff: TermKey, spezifisch: string): string {
  const t = TERMS[begriff];
  const voll = `${t.tip} ${spezifisch}`.trim();
  if (voll.length <= SPALTEN_TIP_MAX || !WAS_WIR_ZAEHLEN.includes(begriff) || !spezifisch.trim()) return voll;
  return `${spezifisch.trim()} Begriff „${t.label}“: siehe „Was wir zählen“ auf der Übersicht.`;
}

/**
 * Tooltip des Etiketts „jung" mit den Schwellen der API (`jung_anteil` als Bruch, z. B. 0,2; `jung_tage`). Ohne
 * Schwellen der allgemeine Satz aus `TERMS.jung`.
 */
export const jungTip = (jungAnteil: number | null, jungTage: number | null): string =>
  jungAnteil === null || jungTage === null
    ? TERMS.jung.tip
    : `Mehr als ${formatNumber(jungAnteil * 100, 0)} % der Anfos dieser Zeile sind jünger als ${formatNumber(jungTage, 0)} Tage. Bis dahin kommen noch erste Zahlungen; die Quote steigt meist noch.`;

/**
 * Leitfragen der Kapitel, die an zwei Stellen stehen (Kapitelseite und Kachel auf der Übersicht P0) — ein Text,
 * sonst laufen sie auseinander (E7.7, P1-V3 § 2.1).
 */
export const LEITFRAGEN = {
  webinareProdukte: 'Was sollte nächsten Sonntag laufen — und was haben die letzten Webinare und Produkte gebracht?',
  // P5-Review #8: „Woher kommen die Bestellungen" — gezählt werden Anfos; „Bestellungen" heißt inkl. verweigerter.
  kanaele: 'Woher kommen die Anfos, und welche Werbefläche bringt je 1.000 Kontakte am meisten?',
} as const;

/** Ein Wort der Wörterzeile: Kennzahl-Wort aus `TERMS.*.glossar` oder Wort ohne Kennzahl aus `GLOSSAR`. */
export type Wort = { term: TermKey } | { glossar: GlossarKey };

/**
 * Wortliste je Kapitel für die Wörterzeile (E7.1, P2-V3 § 4.3). Nur was hier steht, erscheint — ein neuer
 * `glossar`-Satz wächst nicht still in andere Kapitel. Reihenfolge = Anzeigereihenfolge.
 * `kanaele` ist die P3-Liste vom 23.10.2026 (alle damaligen Sätze außer Kanal/Werbefläche/Kennung), unverändert.
 */
export const WOERTERZEILE: Record<'kanaele' | 'webinareProdukte' | 'umsatz', readonly Wort[]> = {
  kanaele: [
    { term: 'anfos' },
    { term: 'showUp' },
    { term: 'kontakteZustellungen' },
    { glossar: 'heatUp' },
    { glossar: 'redlink' },
    { glossar: 'direktMailing' },
    { glossar: 'bestaetigungsseite' },
    { glossar: 'nachfass' },
    { glossar: 'schwestermarken' },
    { glossar: 'tEuro' },
  ],
  // P1-V3 § 2.1 (E7.1) + „geschätzt" (Till 23.10.). „Heat-Up" fehlt, P1 zeigt das Wort nicht mehr.
  webinareProdukte: [
    { term: 'anfos' },
    { term: 'wertBeiBestellung' },
    { glossar: 'kontakte' },
    { term: 'showUp' },
    { term: 'zuSpaetLive' },
    { term: 'eingestiegen' },
    { term: 'teilnehmer' },
    { term: 'bezahlquote' },
    { glossar: 'direktMailing' },
    { glossar: 'termin' },
    { term: 'zahlenOffen' },
    { term: 'vorlaeufig' },
    { glossar: 'upsell' },
    { glossar: 'einmalkauf' },
    { term: 'testkandidat' },
    { glossar: 'mittlereHaelfte' },
    { term: 'wertGeschaetzt' },
    { glossar: 'tEuro' },
  ],
  // P2-V3 § 2.1 + „geschätzt".
  umsatz: [
    { term: 'anfos' },
    { term: 'bezahlquote' },
    { glossar: 'testphase' },
    { glossar: 'sofortzahlung' },
    { glossar: 'einmalkauf' },
    { term: 'vorlaeufig' },
    { term: 'rechnungsstufe' },
    { term: 'vertragsstand' },
    { term: 'wertBeiBestellung' },
    { term: 'umsatzNetto' },
    { term: 'wertGeschaetzt' },
    { glossar: 'tEuro' },
    { glossar: 'plx' },
    { glossar: 'prozentpunkte' },
  ],
};

/** Satz eines Worts; `null`, wenn der TERMS-Eintrag (noch) keinen `glossar` trägt. */
export const wortSatz = (w: Wort): string | null => {
  if ('glossar' in w) return GLOSSAR[w.glossar];
  const t = TERMS[w.term];
  return 'glossar' in t ? t.glossar : null;
};

/**
 * Welche Bestellungen eine Kennzahl zählt — der „Attributionsvertrag" der Sektion „Anfos & Umsatz".
 * Zwei Zahlen mit verschiedener Zuordnung stehen nie im selben Bruch. Jede Karte nennt ihre.
 * Backend: webinarFacts.ts (Webinar), WebinarSendMetricsService.ts (Erst-Touch je Versand),
 * orderChannel.ts (Banner-Kanal).
 */
export const ATTRIBUTION = {
  webinar: {
    label: 'Alle Webinar-Anfos',
    tip: 'Jede Anfo, die einem Webinar zugeordnet ist — auch Käufe ohne Anmeldung (z. B. über die Aufzeichnung), mehrere Käufe derselben Person und Käufe ohne Kennung im Bestell-Link, wenn die Adresse vorher angemeldet war und höchstens 72 Stunden nach Beginn bestellt hat („über Anmeldung, ohne Kennung“). Gut für „was hat das Webinar gebracht“.',
  },
  ersterKontakt: {
    label: 'Erster Kontakt je Versand',
    tip: 'Die erste Anfo einer Person je Webinar, gebucht auf den Versand, über den sie sich angemeldet hat. Jede Anfo zählt genau einmal, deshalb lassen sich Versände fair vergleichen („Anfos je 1.000 Kontakte“). Weitere Käufe derselben Person zählen hier nicht.',
  },
  ersteAnmeldung: {
    label: 'Erste Anmeldung je Webinar',
    tip: 'Die erste Anfo einer Person je Webinar, gebucht auf die Werbefläche, über die sie sich ZUERST für dieses Webinar angemeldet hat. Dieselbe Regel wie „Erster Kontakt je Versand“, nur über alle Flächen. Ausnahme: Wer zuerst über einen Newsletter-Platz und später über ein Mailing kam, kann bei beiden stehen, weil die Mailing-Zahlen den Erst-Kontakt nur unter den Mailings bestimmen (gemessen 20.10.2026: 0 Anfos). Folge der Regel: Flächen früh im Ablauf (Reaktivierung, Vortag) sammeln die Interessierten zuerst ein — ein niedriger Wert einer späteren Fläche heißt nicht, dass sie verzichtbar ist.',
  },
  bannerFlaeche: {
    label: 'Kennung im Bestell-Link',
    tip: 'Die Anfo gehört der Fläche, deren Kennung im Bestell-Link steht. Ein Kauf kann deshalb in beiden Blöcken stehen — z. B. über einen Heat-Up angemeldet und über den Nachfass-Link gekauft. Die Blöcke werden nie addiert.',
  },
  bannerKanal: {
    label: 'Kanal (aus der Kennung im Bestell-Link)',
    tip: 'Jede Anfo genau einem Kanal zugeordnet, anhand der Kennung im Bestell-Link. Gut für „woher kommen die Bestellungen insgesamt“.',
  },
} as const;

export const KPI = {
  registrations: {
    label: 'Registrierungen',
    short: 'Reg.',
    group: 'Personen',
    format: 'int',
    tooltip:
      'Personen, die sich für das Webinar angemeldet haben. Wer sich mehrfach für dasselbe Webinar anmeldet, zählt einmal. Über mehrere Webinare wird addiert — wer zu zwei Webinaren kommt, zählt zweimal. Enthält auch Anmeldungen nach Sendestart.',
    formel: 'Anzahl verschiedener E-Mail-Adressen je Webinar, über die Webinare summiert',
    quelle: Q_REG,
  },
  distinct_persons: {
    label: 'Verschiedene Personen',
    short: 'Personen',
    group: 'Personen',
    format: 'int',
    tooltip:
      'Wie viele verschiedene Menschen hinter den Registrierungen stehen. Wer sich für mehrere Webinare im Zeitraum angemeldet hat, zählt hier nur einmal — deshalb kleiner als „Registrierungen“.',
    formel: 'Anzahl verschiedener E-Mail-Adressen über alle Webinare im Zeitraum',
    quelle: Q_REG,
  },
  timely: {
    label: 'Rechtzeitig angemeldet',
    short: 'Rechtz.',
    group: 'Personen',
    format: 'int',
    tooltip:
      'Registrierungen, die VOR Sendebeginn eingingen. Wer sich erst nach Start einträgt, wird automatisch auf die Aufzeichnung geleitet und kann nicht live dabei sein — deshalb ist das der Nenner der Show-up-Rate.' +
      OHNE_WARNUNG,
    formel: `Personen, deren erste Anmeldung vor dem Sendestart lag${OHNE_WARNUNG_FORMEL}`,
    quelle: Q_REG,
  },
  attendees: {
    label: 'Teilnehmer',
    short: 'Teiln.',
    group: 'Personen',
    format: 'int',
    tooltip:
      'Angemeldete Personen, die nachweislich live im Webinar waren (Player geöffnet, Angebot gesehen, früher gegangen o. ä.). Jede Person zählt einmal, auch wenn sie mit zwei Geräten dabei war.' +
      OHNE_WARNUNG,
    formel: `Personen mit mindestens einem Anwesenheits-Signal (Teilnahme, später dazu, früher gegangen, bis zur Marke geblieben, Angebot gesehen/geklickt)${OHNE_WARNUNG_FORMEL}`,
    quelle: Q_EVENTS,
  },
  show_up_rate: {
    label: 'Show-up-Rate',
    short: 'Show-up',
    group: 'Quoten',
    format: 'pct',
    tooltip:
      'Wie viele der rechtzeitig Angemeldeten live dabei waren. Anmeldungen nach Sendestart zählen hier nicht mit, weil sie gar nicht live dabei sein konnten. Über mehrere Webinare gerechnet zählen große Webinare entsprechend mehr.' +
      OHNE_WARNUNG,
    formel: `Teilnehmer ÷ rechtzeitig Angemeldete × 100${OHNE_WARNUNG_FORMEL}`,
    quelle: `${Q_REG}; ${Q_EVENTS}`,
  },
  orders: {
    label: 'Anfos',
    short: 'Anfos',
    group: 'Anfos',
    format: 'int',
    tooltip:
      'Bestellungen, die diesem Webinar zugeordnet sind (Anforderungen). Stornierte Bestellungen zählen nicht. Enthält auch Käufe ohne Anmeldung.',
    formel: 'Anzahl zugeordneter, nicht stornierter Webinar-Bestellungen',
    quelle: Q_ORDERS,
  },
  orders_with_registration: {
    label: 'Anfos von Registrierten',
    short: 'Anfos m. Anm.',
    group: 'Anfos',
    format: 'int',
    tooltip:
      'Anfos, zu denen wir die Anmeldung des Käufers kennen. Der Rest wurde dem Webinar über Banner oder Link zugeordnet, ohne dass eine Anmeldung gefunden wurde.',
    formel: 'Anfos, deren Zuordnung (RID oder E-Mail) eine Anmeldung zu diesem Webinar trifft',
    quelle: Q_ORDERS,
  },
  attendee_orders: {
    label: 'Anfos von Teilnehmern',
    short: 'Anfos Teiln.',
    group: 'Anfos',
    format: 'int',
    tooltip: 'Anfos von Personen, die live im Webinar waren.' + OHNE_WARNUNG,
    formel: `Anfos, deren Käufer ein Teilnehmer dieses Webinars ist${OHNE_WARNUNG_FORMEL}`,
    quelle: `${Q_ORDERS}; ${Q_EVENTS}`,
  },
  registration_conversion_rate: {
    label: 'Anfos je Registrierung',
    short: 'Anfos/Reg.',
    group: 'Quoten',
    format: 'pct',
    decimals: 2,
    tooltip: 'Anfos im Verhältnis zu allen Registrierungen.',
    formel: 'Anfos ÷ Registrierungen × 100',
    quelle: `${Q_ORDERS}; ${Q_REG}`,
  },
  attendee_conversion_rate: {
    label: 'Anfos je Teilnehmer',
    short: 'Anfos/Teiln.',
    group: 'Quoten',
    format: 'pct',
    decimals: 2,
    tooltip:
      'Wie viele der Live-Teilnehmer gekauft haben. Käufe von Personen, die nicht im Webinar waren, zählen hier nicht.' +
      OHNE_WARNUNG,
    formel: `Anfos von Teilnehmern ÷ Teilnehmer × 100${OHNE_WARNUNG_FORMEL}`,
    quelle: `${Q_ORDERS}; ${Q_EVENTS}`,
  },
  cpa_revenue: {
    label: 'CPA-Wert',
    short: 'CPA-Wert',
    group: 'Umsatz',
    format: 'eur',
    decimals: 0,
    color: KPI_COLORS.cpa,
    tooltip:
      'Die Steuerungsgröße für Kampagnen: jede Anfo mit ihrem CPA-Wert bewertet (der Betrag, den eine Anfo für die Akquise wert ist). Seit 13.10.2026 trägt jede Anfo ihren eigenen Wert: bis ihre Zahlung zählt den ihres Angebots (Zahlung vorab deutlich höher als mit Testphase), danach den tatsächlich gezahlten Betrag hochgerechnet — eine nie bezahlte Anfo zählt dann 0 €. Die Werte ändern sich deshalb täglich. Kein Zahlungseingang — dafür „Umsatz (bezahlt)“. Anfos, deren Produkt keinen CPA-Wert hat, sind nicht enthalten und werden daneben ausgewiesen.',
    formel: 'Summe der CPA-Werte aller Anfos (je Anfo: Wert ihrer Zahlung, sonst ihres Angebots, sonst ihres Produkts)',
    quelle: `${Q_ORDERS}; CPA-Wert aus plx_anfo_wert_order, plx_anfo_wert_offer, plx_products.anfo_cpa (in dieser Reihenfolge)`,
  },
  orders_without_cpa: {
    label: 'Anfos ohne CPA-Wert',
    short: 'ohne CPA',
    group: 'Anfos',
    format: 'int',
    tooltip:
      'Anfos, deren Produkt keinen CPA-Wert hat oder deren Artikel PLX noch nicht geliefert hat. Sie fehlen im „CPA-Wert“.',
    formel: 'Anfos ohne CPA-Wert',
    quelle: Q_ORDERS,
  },
  cpa_per_registration: {
    label: 'CPA-Wert je Registrierung',
    short: 'CPA-Wert/Reg.',
    group: 'Umsatz',
    format: 'eur',
    color: KPI_COLORS.cpa,
    tooltip: 'Was eine Registrierung im Schnitt an CPA-Wert bringt.',
    formel: 'CPA-Wert ÷ Registrierungen',
    quelle: `${Q_ORDERS}; ${Q_REG}`,
  },
  cpa_per_attendee: {
    label: 'CPA-Wert je Teilnehmer',
    short: 'CPA-Wert/Teiln.',
    group: 'Umsatz',
    format: 'eur',
    color: KPI_COLORS.cpa,
    tooltip:
      'CPA-Wert geteilt durch Live-Teilnehmer. Achtung: enthält auch Käufe von Nicht-Teilnehmern. In Summen zählen CPA-Wert und Teilnehmer beide ohne Webinare mit Tracking-Warnung, damit Zähler und Nenner zusammenpassen.',
    formel: `CPA-Wert ÷ Teilnehmer${OHNE_WARNUNG_FORMEL}`,
    quelle: `${Q_ORDERS}; ${Q_EVENTS}`,
  },
  avg_cpa_per_order: {
    label: 'Ø CPA-Wert je Anfo',
    short: 'Ø CPA-Wert',
    group: 'Umsatz',
    format: 'eur',
    color: KPI_COLORS.cpa,
    tooltip: 'Durchschnittlicher CPA-Wert einer Anfo (nur Anfos mit CPA-Wert).',
    formel: 'CPA-Wert ÷ Anfos mit CPA-Wert',
    quelle: Q_ORDERS,
  },
  ltv_revenue: {
    label: 'Anfo-Wert',
    short: 'Anfo-Wert',
    group: 'Umsatz',
    format: 'eur',
    decimals: 0,
    color: KPI_COLORS.ltv,
    tooltip:
      'Nebenwert: jede Anfo mit ihrem Anfo-Wert bewertet (erwarteter Zahlungseingang über 36 Monate). Seit 13.10.2026 trägt jede Anfo ihren eigenen Wert: bis ihre Zahlung zählt den ihres Angebots (Zahlung vorab deutlich höher als mit Testphase), danach den tatsächlich gezahlten Betrag hochgerechnet — eine nie bezahlte Anfo zählt dann 0 €. Die Werte ändern sich deshalb täglich. Zeigt das Potenzial, nicht den Akquise-Wert — deshalb höher als der CPA-Wert.',
    formel: 'Summe der Anfo-Werte aller Anfos (je Anfo: Wert ihrer Zahlung, sonst ihres Angebots, sonst ihres Produkts)',
    quelle: `${Q_ORDERS}; Anfo-Wert aus plx_anfo_wert_order, plx_anfo_wert_offer, plx_products.anfo_wert (in dieser Reihenfolge)`,
  },
  ltv_per_registration: {
    label: 'Anfo-Wert je Registrierung',
    short: 'Anfo-Wert/Reg.',
    group: 'Umsatz',
    format: 'eur',
    color: KPI_COLORS.ltv,
    tooltip: 'Anfo-Wert je Registrierung.',
    formel: 'Anfo-Wert ÷ Registrierungen',
    quelle: `${Q_ORDERS}; ${Q_REG}`,
  },
  ltv_per_attendee: {
    label: 'Anfo-Wert je Teilnehmer',
    short: 'Anfo-Wert/Teiln.',
    group: 'Umsatz',
    format: 'eur',
    color: KPI_COLORS.ltv,
    tooltip:
      'Anfo-Wert je Live-Teilnehmer (enthält auch Käufe von Nicht-Teilnehmern). In Summen beide Seiten ohne Webinare mit Tracking-Warnung.',
    formel: `Anfo-Wert ÷ Teilnehmer${OHNE_WARNUNG_FORMEL}`,
    quelle: `${Q_ORDERS}; ${Q_EVENTS}`,
  },
  avg_ltv_per_order: {
    label: 'Ø Anfo-Wert je Anfo',
    short: 'Ø Anfo-Wert',
    group: 'Umsatz',
    format: 'eur',
    color: KPI_COLORS.ltv,
    tooltip: 'Durchschnittlicher Anfo-Wert einer Anfo.',
    formel: 'Anfo-Wert ÷ Anfos mit Anfo-Wert',
    quelle: Q_ORDERS,
  },
  paid_revenue: {
    label: 'Umsatz (bezahlt)',
    short: 'Umsatz',
    group: 'Umsatz',
    format: 'eur',
    decimals: 0,
    color: KPI_COLORS.paid,
    tooltip:
      'Tatsächlich bezahlter Umsatz dieser Anfos bis heute, Gutschriften abgezogen; eine Testphasen-Rechnung bis 1 € macht aus einer Anfo kein bezahltes Abo, der Betrag ist aber im Umsatz enthalten. Bei frischen Webinaren niedrig, weil Testphasen noch laufen und Rechnungen noch nicht bezahlt sind.',
    formel: 'Summe der bisher bezahlten Beträge je Anfo (nach Abzug von Gutschriften)',
    quelle: 'PLX-Zahlungsdaten (plx_order_metrics.actual_revenue)',
  },
  paid_per_registration: {
    label: 'Umsatz je Registrierung',
    short: 'Umsatz/Reg.',
    group: 'Umsatz',
    format: 'eur',
    color: KPI_COLORS.paid,
    tooltip: 'Bisher bezahlter Umsatz je Registrierung.',
    formel: 'Umsatz (bezahlt) ÷ Registrierungen',
    quelle: 'PLX-Zahlungsdaten; Anmeldungen',
  },
  avg_paid_per_order: {
    label: 'Ø Umsatz je Anfo',
    short: 'Ø Umsatz',
    group: 'Umsatz',
    format: 'eur',
    color: KPI_COLORS.paid,
    tooltip: 'Bisher bezahlter Umsatz je Anfo.',
    formel: 'Umsatz (bezahlt) ÷ Anfos',
    quelle: 'PLX-Zahlungsdaten',
  },
  upsell_orders: {
    label: 'Upsells',
    short: 'Upsells',
    group: 'Anfos',
    format: 'int',
    tooltip: 'Anfos, deren Werbeaktion als Upsell (z. B. Jahresabo ohne Testphase) hinterlegt ist.',
    formel: 'Anfos mit Upsell-Kennzeichen in der Werbeaktions-Konfiguration',
    quelle: 'plx_werbeaktion_config (is_upsell)',
  },
  upsell_rate: {
    label: 'Upsell-Rate',
    short: 'Upsell %',
    group: 'Quoten',
    format: 'pct',
    tooltip: 'Anteil der Upsells an allen Anfos.',
    formel: 'Upsells ÷ Anfos × 100',
    quelle: 'plx_werbeaktion_config',
  },
  active_subscribers: {
    label: 'Aktive Abos',
    short: 'Akt. Abos',
    group: 'Anfos',
    format: 'int',
    tooltip: 'Anfos, die heute ein laufendes, bezahltes Abo sind (nicht mehr in der Testphase).',
    formel: 'Anfos mit Vertragsstatus „laufend“ und Status bezahlt oder Upsell',
    quelle: 'PLX-Vertragsstatus',
  },
  active_subscriber_rate: {
    label: 'Aktive Abo-Rate',
    short: 'Akt. Abo %',
    group: 'Quoten',
    format: 'pct',
    tooltip: 'Anteil der aktiven bezahlten Abos an allen Anfos.',
    formel: 'Aktive Abos ÷ Anfos × 100',
    quelle: 'PLX-Vertragsstatus',
  },
  unique_customers: {
    label: 'Kunden',
    short: 'Kunden',
    group: 'Anfos',
    format: 'int',
    tooltip:
      'Verschiedene Käufer (E-Mail-Adressen). Anfos, zu denen PLX die E-Mail noch nicht geliefert hat, fehlen hier vorübergehend. In der Summenzeile zählt ein Käufer mehrerer Webinare einmal.',
    formel: 'Anzahl verschiedener Käufer-E-Mails',
    quelle: Q_ORDERS,
  },
  cta_views: {
    label: 'Angebot gesehen',
    short: 'CTA gesehen',
    group: 'Engagement',
    format: 'int',
    tooltip:
      'Personen, denen im Webinar-Player das Kaufangebot angezeigt wurde — oder die darauf geklickt haben (der Klick beweist die Einblendung).',
    formel: 'Personen mit Signal „Angebot gesehen“ oder „Angebot geklickt“',
    quelle: Q_EVENTS,
  },
  cta_clicks: {
    label: 'Angebot geklickt',
    short: 'CTA geklickt',
    group: 'Engagement',
    format: 'int',
    tooltip: 'Personen, die im Player auf das Kaufangebot geklickt haben.',
    formel: 'Personen mit Signal „Angebot geklickt“',
    quelle: Q_EVENTS,
  },
  cta_ctr: {
    label: 'Angebots-Klickrate',
    short: 'CTA-Rate',
    group: 'Quoten',
    format: 'pct',
    decimals: 2,
    tooltip:
      'Wie viele derjenigen, die das Angebot gesehen haben, darauf geklickt haben. Wer geklickt hat, zählt immer auch als „gesehen“ — die Einblendung wird nicht bei jedem Zuschauer gemessen.',
    formel: 'Angebot geklickt ÷ (Angebot gesehen oder geklickt) × 100',
    quelle: Q_EVENTS,
  },
  cta_buy_rate: {
    label: 'Käufer unter den Klickern',
    short: 'Klick → Kauf',
    group: 'Quoten',
    format: 'pct',
    decimals: 1,
    tooltip:
      'Wie viele der Personen, die im Webinar auf das Angebot geklickt haben, eine Anfo abgegeben haben. Ohne zeitliche Reihenfolge: wer erst bestellt und danach klickt, zählt mit.',
    formel: 'Personen mit Klick und Anfo ÷ Personen mit Klick × 100',
    quelle: Q_EVENTS,
  },
} satisfies Record<string, KpiDefinition>;

export type KpiKey = keyof typeof KPI;

const DEFAULT_DECIMALS: Record<KpiFormat, number> = { int: 0, eur: 2, pct: 1 };

/** Einheitliche Darstellung nach Format. `null`/`undefined` = nicht berechenbar → „–“ (nie 0). */
export const formatByFormat = (
  format: KpiFormat,
  value: number | null | undefined,
  decimals?: number,
): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '–';
  const n = formatNumber(Number(value), decimals ?? DEFAULT_DECIMALS[format]);
  if (format === 'eur') return `${n} €`;
  if (format === 'pct') return `${n} %`;
  return n;
};

/** Einheitliche Darstellung einer Webinar-KPI. */
export const formatKpi = (key: KpiKey, value: number | null | undefined): string => {
  const def: KpiDefinition = KPI[key];
  return formatByFormat(def.format, value, def.decimals);
};

/** Prozentwert (Eingabe IST bereits Prozent, z. B. 12.5 → „12,5 %“) — nie ein Bruch. */
export const formatPct = (value: number | null | undefined, decimals = 1): string =>
  formatByFormat('pct', value, decimals);

/** Euro-Betrag. */
export const formatEur = (value: number | null | undefined, decimals = 2): string =>
  formatByFormat('eur', value, decimals);

/**
 * Sonderzeilen in Gruppierungstabellen — keine Kanaele, sondern Restposten. Stehen immer am Ende
 * und erklaeren sich per Tooltip selbst.
 */
export const CATEGORY_HINTS: Record<string, string> = {
  'Ohne Anmeldung':
    'Anfos, die dem Webinar zugeordnet sind (über Banner oder Link), zu denen aber keine Anmeldung gefunden wurde. Häufigster Grund: PLX liefert die E-Mail zur Bestellung oft erst einige Tage später nach — bei frischen Webinaren ist diese Zeile daher größer als in einer Woche.',
  'Kauf nach Sendestart, ohne Anmeldung':
    'Alte Fälle (bis 25.08.2026): Kauf nach Sendebeginn, ohne Anmeldung. Wer sich damals nach Start eintrug, wurde ohne Anmeldezeile auf die Aufzeichnung geleitet. Seitdem wird die Anmeldung gespeichert und neue Fälle stehen beim richtigen Kanal.',
  'Ohne Teilnahme':
    'Anfos von Personen, die nicht live im Webinar waren (z. B. über Aufzeichnung oder Nachfass-Mail gekauft).',
  'Livestream Auto-Roll':
    'Anfos über den Kaufknopf, wenn das Webinar nach dem Livestream als Auto-Roll lief (Banner endet auf _KG) — von Personen ohne Webinar-Anmeldung. Wer angemeldet war, steht beim Kanal seiner Anmeldung.',
  'Teilnahme nicht belastbar':
    'Anfos von Personen aus Webinaren mit Tracking-Warnung. Dort hat StealthSeminar kaum Teilnahme-Signale geliefert — ob die Person live dabei war, lässt sich nicht sagen.',
  Sonstige:
    'Zusammengefasst: Kanäle bzw. Affiliates mit sehr wenigen Registrierungen, damit die Tabelle lesbar bleibt.',
  Unbekannt:
    'Kein Gerät erkennbar — z. B. Anmeldungen, bei denen der Browser keine Angabe mitgeschickt hat.',
  '(kein Affiliate)': 'Anmeldungen ohne Affiliate-Kennung im Link.',
  '(kein Artikel)': 'Anfos, zu denen PLX den Artikel noch nicht geliefert hat.',
};

export const TAIL_CATEGORIES = [
  'Sonstige',
  '(kein Affiliate)',
  'Unbekannt',
  'Kauf nach Sendestart, ohne Anmeldung',
  'Ohne Anmeldung',
  'Ohne Teilnahme',
  'Teilnahme nicht belastbar',
];

/** Grundbegriffe fuer das Formelblatt und die „So lesen Sie diese Seite“-Box. */
export const KPI_CONCEPTS: { titel: string; text: string }[] = [
  {
    titel: 'Welcher Zeitraum?',
    text: 'Maßgeblich ist das Webinar-Datum. Ein Webinar im Zeitraum bringt alle seine Registrierungen und alle seine Anfos mit — auch Käufe, die Tage später kamen.',
  },
  {
    titel: 'Wie kommt eine Bestellung zum Webinar?',
    text: 'Jede Bestellung wird genau einem Webinar zugeordnet (über den Banner- bzw. Link-Code und die Anmeldung). Die Zuordnung ist für alle Tabellen dieselbe, eine Anfo kann nie in zwei Webinaren gleichzeitig zählen.',
  },
  {
    titel: 'Wie kommt eine Bestellung zur Anmeldung?',
    text: 'Über die persönliche Kennung im Bestelllink (RID), sonst über die E-Mail-Adresse. Findet sich keine Anmeldung, steht die Anfo in „Ohne Anmeldung“.',
  },
  {
    titel: 'Welcher Kanal bekommt die Person?',
    text: 'Der Kanal (Ad-Type, Blog, Affiliate, Gerät) ihrer ERSTEN Anmeldung zu diesem Webinar. Meldet sich jemand über Heat-Up und später über den Newsletter an, zählt die Person beim Heat-Up. So summieren sich die Kanäle genau auf die Gesamtzahl.',
  },
  {
    titel: 'Umsatz, CPA-Wert, Anfo-Wert',
    text: '„Umsatz (bezahlt)“ ist der tatsächlich bezahlte Umsatz, Gutschriften abgezogen; eine Testphasen-Rechnung bis 1 € ist darin enthalten, macht die Anfo aber nicht zum bezahlten Abo. „CPA-Wert“ bewertet jede Anfo mit ihrem Akquise-Wert (seit 13.10.2026 je Bestellung: bis ihre Zahlung zählt nach ihrem Angebot, danach nach dem gezahlten Betrag; nie bezahlt = 0 €) — die Steuerungsgröße für Kampagnen, kein Geldeingang. „Anfo-Wert“ ist der erwartete Zahlungseingang einer Anfo über 36 Monate (Potenzial, täglich aus den Zahlungen nachgezogen). Dieselben drei Wörter bedeuten auf /plx-orders, /anfo-statistics, /webinar-mailings, /plx-mailings, /welcome-campaign und /sam-timeline dasselbe — in der Sektion /auswertungen netto (rund 6 % niedriger), sonst brutto.',
  },
  {
    titel: 'Tracking-Warnung',
    text: 'Liefert StealthSeminar für ein Webinar kaum Teilnahme-Signale (weniger als 10 % der rechtzeitig Angemeldeten bei mindestens 50 Anmeldungen) oder mehr Teilnehmer als Angemeldete, ist die Teilnahme dort nicht belastbar. Das Webinar wird markiert; seine rechtzeitig Angemeldeten, Teilnehmer und Anfos von Teilnehmern zählen in keiner Summe, Kachel oder Kanal-Tabelle mit — nur in seiner eigenen Zeile und auf seiner Detailseite. Registrierungen, Anfos, CPA-Wert und Umsatz zählen überall normal.',
  },
  {
    titel: 'Filter',
    text: 'Affiliate-, Adref-, Ad-Type- und Blog-Filter wählen Personen (nach ihrer ersten Anmeldung) und damit deren Anfos — Anfos ohne Anmeldung fallen dabei heraus. Der Produktfilter wählt nur Anfos; die Registrierungen bleiben unverändert.',
  },
];

// --- P1 --- (weitere Exporte des P1-Bauers; neue TERMS-Schlüssel gehören in den P1-Block IN `TERMS`)

// --- P2 --- (weitere Exporte des P2-Bauers; neue TERMS-Schlüssel gehören in den P2-Block IN `TERMS`)
/** Leitfrage des Kapitels „Umsatz & Bezahlqualität" (P2-V3 § 2.1) — für Kapitelseite und Übersicht P0 (E7.7). */
export const LEITFRAGE_UMSATZ = 'Wie viele Anfos werden bezahlt, wer zahlt nicht, und welche Rechnungen kommen als Nächstes?';

// --- P4 --- (weitere Exporte des P4-Bauers; neue TERMS-Schlüssel gehören in den P4-Block IN `TERMS`)
/** Leitfrage des Kapitels „Hebel" — für Kapitelseite und Übersicht P0 (ein Text, wie LEITFRAGE_UMSATZ). */
export const HEBEL_FRAGE = 'Was bringen die Einladungs-Wellen, der Nachfass je Publisher und Kunden mit mehreren Anfos?';
